import { useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { Bar, Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import {
  CLIMATE_DEFAULT_OPACITY,
  CLIMATE_MAX_OPACITY,
  CLIMATE_MIN_OPACITY,
  CLIMATE_MONTH_LABELS,
  CLIMATE_Y_OFFSET,
  HORIZON_COLORS,
  applyClimateStationTransform,
  climateDomain,
  climateValueAt,
  climateValues,
  createClimateInterpolator,
  createClimateStations,
  formatClimateValue,
  horizonLabel,
  paintClimateStations,
  parseClimateAxes,
  periodLabel,
  stationMonthlySeries,
  stationScalars,
} from "../terrain3d/climateLayer";
import {
  COMMUNE_BORDER_Y_OFFSET,
  COMMUNE_CHOROPLETH_Y_OFFSET,
  COMMUNE_DEFAULT_OPACITY,
  COMMUNE_MAX_OPACITY,
  COMMUNE_MIN_OPACITY,
  COMMUNE_NODATA,
  applyDrapedExaggeration,
  buildCommuneBorders,
  buildCommuneCellIndex,
  communeHoverColor,
  createTerrainPicker,
  paintCells,
  paintRasterColors,
} from "../terrain3d/communesLayer";
import {
  GEOLOGY_DEFAULT_OPACITY,
  GEOLOGY_MAX_OPACITY,
  GEOLOGY_MIN_OPACITY,
  GEOLOGY_Y_OFFSET,
  geologyEntityAt,
} from "../terrain3d/geologyLayer";
import {
  DISCHARGE_MONTH_LABELS,
  applyDischargeStationTransform,
  createDischargeStations,
  dischargeValueAt,
  formatDischargeValue,
  paintDischargeStations,
  parseDischargeAxes,
  stationMonthlyDischarge,
} from "../terrain3d/dischargeLayer";
import {
  buildLegend,
  findIndicator,
  formatIndicatorValue,
  formatNumber,
  groupedIndicators,
  rampCss,
  resolveIndicator,
} from "../terrain3d/indicatorEngine";
import { territoryUrl } from "../terrain3d/terrainBase";
import {
  DIORAMA_KINDS,
  pickDioramaSpots,
  buildDiorama,
  createDioramaPin,
  disposeDioramaPin,
} from "./dioramas";
import "./TerrainTestPage.css";

// Enregistrement explicite (idempotent) : ne pas dependre du fait qu'un autre
// composant de l'appli ait deja enregistre ce plugin globalement.
ChartJS.register(
  CategoryScale, LinearScale, BarElement,
  LineController, LineElement, PointElement,
  Tooltip, ChartDataLabels
);

/**
 * Page de test ISOLEE : visualisation 3D d'un extrait de MNT (IGN BD ALTI)
 * decoupe par un contour geojson, pour explorer le rendu "jeu video /
 * strategie" d'un territoire.
 *
 * Rien ici n'est branche a la base de donnees ni aux ateliers : les donnees
 * viennent de fichiers statiques generes par
 * api/experiments/terrain3d/build_heightmap.py et servis depuis l'Object Storage
 * (cf. terrain3d/terrainBase.js). Route non listee dans le menu : /terrain-test.
 */

const DATA_URL = territoryUrl("ardeche");

// Rampe hypsometrique : basse altitude (fond de vallee / cours d'eau) vers
// haute altitude (crete calcaire). Chaque stop = [t, [r,g,b] 0-255].
const ELEVATION_RAMP = [
  [0.0, [43, 98, 92]],
  [0.15, [72, 128, 68]],
  [0.38, [123, 148, 74]],
  [0.6, [150, 122, 84]],
  [0.82, [150, 143, 132]],
  [1.0, [238, 232, 216]],
];

// Teinte du "socle" (mur + base) : terre/roche, degrade clair -> sombre.
const WALL_TOP_COLOR = [120, 98, 76];
const WALL_BOTTOM_COLOR = [46, 38, 32];

function rampColor(t, ramp) {
  t = Math.min(1, Math.max(0, t));
  for (let i = 0; i < ramp.length - 1; i++) {
    const [t0, c0] = ramp[i];
    const [t1, c1] = ramp[i + 1];
    if (t >= t0 && t <= t1) {
      const f = (t - t0) / (t1 - t0 || 1);
      return [
        (c0[0] + (c1[0] - c0[0]) * f) / 255,
        (c0[1] + (c1[1] - c0[1]) * f) / 255,
        (c0[2] + (c1[2] - c0[2]) * f) / 255,
      ];
    }
  }
  const last = ramp[ramp.length - 1][1];
  return [last[0] / 255, last[1] / 255, last[2] / 255];
}

// Rampe pluviometrie (TEST, donnees synthetiques), convention "carte meteo" :
// sec = jaune/beige -> vert -> bleu -> violet pour les zones les plus
// arrosees. Contrairement a un multiply-blend blanc=neutre, chaque niveau a
// une couleur visible, y compris la zone seche (overlay semi-transparent en
// alpha-blend classique, cf. rainfallMaterial).
const RAINFALL_RAMP = [
  [0.0, [224, 196, 128]],
  [0.3, [214, 214, 120]],
  [0.55, [125, 195, 130]],
  [0.8, [70, 140, 210]],
  [1.0, [115, 60, 180]],
];

function lerpColor(c0, c1, f) {
  return [
    (c0[0] + (c1[0] - c0[0]) * f) / 255,
    (c0[1] + (c1[1] - c0[1]) * f) / 255,
    (c0[2] + (c1[2] - c0[2]) * f) / 255,
  ];
}

// Profondeur (en metres reels, non exageres) du socle sous le point le plus
// bas du terrain.
const SOCLE_DEPTH_RATIO = 0.16; // x zRange, sous le point le plus bas

// --- Rivieres : rendu generaliste (fonctionne avec n'importe quel nombre
// de cours d'eau, chacun pouvant avoir plusieurs polylignes disjointes) ---
const RIVER_COLOR = "#8fd8ee";

function createFlowTexture() {
  const canvas = document.createElement("canvas");
  canvas.width = 64;
  canvas.height = 8;
  const ctx = canvas.getContext("2d");
  const grad = ctx.createLinearGradient(0, 0, 64, 0);
  grad.addColorStop(0, "rgba(200,240,255,0.1)");
  grad.addColorStop(0.5, "rgba(225,250,255,0.95)");
  grad.addColorStop(1, "rgba(200,240,255,0.1)");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, 64, 8);
  const tex = new THREE.CanvasTexture(canvas);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  return tex;
}

// Normale (perpendiculaire a la tangente locale) en chaque point d'une
// polyligne echantillonnee, precalculee une fois : reutilisee a la fois pour
// la construction initiale du ruban et pour les mises a jour de largeur
// (slider), qui ne refont jamais ce calcul, seulement l'offset x/z.
function computeRiverNormals(points) {
  const n = points.length;
  const normals = new Float32Array(n * 2); // [nx0, nz0, nx1, nz1, ...]
  for (let i = 0; i < n; i++) {
    const prev = points[Math.max(0, i - 1)];
    const next = points[Math.min(n - 1, i + 1)];
    let tx = next.x - prev.x;
    let tz = next.z - prev.z;
    const tLen = Math.hypot(tx, tz) || 1;
    tx /= tLen;
    tz /= tLen;
    normals[i * 2] = -tz;
    normals[i * 2 + 1] = tx;
  }
  return normals;
}

// Construit un ruban (triangle strip) suivant une serie de points deja
// echantillonnes sur le terrain ({x, z, rawH}). Seul rawH depend de
// l'exageration -> on ne stocke/recalcule que la coordonnee Y a chaque
// changement de slider vertical, jamais X/Z (voir applyRiverWidth pour la
// largeur, mise a jour independamment).
function buildRiverRibbon(points, normals, halfWidth, bandWorldLength, exaggeration) {
  const n = points.length;
  const positions = new Float32Array(n * 2 * 3);
  const uvs = new Float32Array(n * 2 * 2);
  const rawH = new Float32Array(n * 2);
  let dist = 0;

  for (let i = 0; i < n; i++) {
    const p = points[i];
    const nx = normals[i * 2];
    const nz = normals[i * 2 + 1];

    if (i > 0) dist += Math.hypot(p.x - points[i - 1].x, p.z - points[i - 1].z);
    const u = dist / bandWorldLength;

    const li = i * 2;
    const ri = i * 2 + 1;
    positions[li * 3] = p.x + nx * halfWidth;
    positions[li * 3 + 2] = p.z + nz * halfWidth;
    positions[ri * 3] = p.x - nx * halfWidth;
    positions[ri * 3 + 2] = p.z - nz * halfWidth;
    rawH[li] = p.rawH;
    rawH[ri] = p.rawH;
    uvs[li * 2] = u; uvs[li * 2 + 1] = 0;
    uvs[ri * 2] = u; uvs[ri * 2 + 1] = 1;
  }

  const indices = [];
  for (let i = 0; i < n - 1; i++) {
    const a = i * 2, b = i * 2 + 1, c = i * 2 + 2, d = i * 2 + 3;
    indices.push(a, c, b, b, c, d);
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute("uv", new THREE.BufferAttribute(uvs, 2));
  geometry.setIndex(indices);
  applyRiverExaggeration(geometry, rawH, exaggeration);
  return { geometry, rawH };
}

function applyRiverExaggeration(geometry, rawH, exaggeration) {
  const pos = geometry.attributes.position;
  const offsetRaw = 3; // leger decollage au-dessus du terrain (evite le z-fighting)
  for (let i = 0; i < rawH.length; i++) {
    pos.setY(i, (rawH[i] + offsetRaw) * exaggeration);
  }
  pos.needsUpdate = true;
}

// Recalcule uniquement l'offset x/z (largeur) d'un ruban deja construit, a
// partir des normales precalculees : pas besoin de reconstruire uv/index, ni
// de toucher a Y (gere independamment par applyRiverExaggeration).
function applyRiverWidth(geometry, points, normals, halfWidth) {
  const pos = geometry.attributes.position;
  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    const nx = normals[i * 2];
    const nz = normals[i * 2 + 1];
    const li = i * 2;
    const ri = i * 2 + 1;
    pos.setX(li, p.x + nx * halfWidth);
    pos.setZ(li, p.z + nz * halfWidth);
    pos.setX(ri, p.x - nx * halfWidth);
    pos.setZ(ri, p.z - nz * halfWidth);
  }
  pos.needsUpdate = true;
}

// Distance point-segment en 2D (x,z). Sert au survol des rivieres : plutot
// qu'un mesh de "hit" dedie par ruban (aurait double le nombre de meshes/
// geometries rivieres, deja nombreux), on cherche simplement le segment le
// plus proche du point vise au sol parmi les points deja echantillonnes pour
// le rendu -> aucune geometrie supplementaire.
function distanceToSegment2D(px, pz, ax, az, bx, bz) {
  const dx = bx - ax;
  const dz = bz - az;
  const lenSq = dx * dx + dz * dz || 1;
  let t = ((px - ax) * dx + (pz - az) * dz) / lenSq;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (ax + t * dx), pz - (az + t * dz));
}

// Cherche, parmi tous les rubans, le plus proche du point (x,z) vise au sol,
// dans la limite de sa propre tolerance (basee sur sa largeur actuelle, donc
// coherente avec le slider d'epaisseur). Retourne les infos de la riviere
// (name, length_km) ou null.
function findHoveredRiver(riverRibbons, x, z, worldDiag) {
  let best = null;
  let bestDist = Infinity;
  for (const ribbon of riverRibbons) {
    const tolerance = Math.max(ribbon.currentHalfWidth * 2.5, worldDiag * 0.0012);
    const pts = ribbon.points;
    for (let i = 0; i < pts.length - 1; i++) {
      const d = distanceToSegment2D(x, z, pts[i].x, pts[i].z, pts[i + 1].x, pts[i + 1].z);
      if (d < bestDist && d <= tolerance) {
        bestDist = d;
        best = ribbon.river;
      }
    }
  }
  return best;
}

// --- Villes : marqueurs interactifs (halo au sol + faisceau + etiquette) ---
const CITY_TIERS = {
  capital: { color: "#db6c52", ring: 1.6, beam: 1.7 },
  subcapital: { color: "#54a8ba", ring: 1.25, beam: 1.35 },
  town: { color: "#fdedc4", ring: 1, beam: 1 },
};

// Les villes sont desormais derivees des communes par population
// (build_overlays.py::derive_cities) et portent leur `tier` directement. Le
// classement par statut administratif reste en repli pour les jeux de
// donnees anterieurs, ou seul un libelle editorial etait disponible.
function classifyCity(city) {
  if (city?.tier && CITY_TIERS[city.tier]) return city.tier;
  const s = (city?.statut || "").toLowerCase();
  if (s.includes("préfecture") && !s.includes("sous")) return "capital";
  if (s.includes("sous-préfecture")) return "subcapital";
  return "town";
}

function roundRectPath(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function createLabelSprite(text, accentColor) {
  const canvas = document.createElement("canvas");
  canvas.width = 512;
  canvas.height = 128;
  const ctx = canvas.getContext("2d");
  ctx.font = "700 46px 'GTWalsheimPro-Condensed', sans-serif";
  const textWidth = ctx.measureText(text).width;
  const boxW = Math.min(canvas.width - 8, textWidth + 64);
  const boxX = (canvas.width - boxW) / 2;
  roundRectPath(ctx, boxX, 32, boxW, 64, 18);
  ctx.fillStyle = "rgba(10,26,36,0.78)";
  ctx.fill();
  ctx.lineWidth = 3;
  ctx.strokeStyle = accentColor;
  ctx.stroke();
  ctx.fillStyle = "#f4f8f9";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(text, canvas.width / 2, 32 + 32);

  const tex = new THREE.CanvasTexture(canvas);
  const mat = new THREE.SpriteMaterial({ map: tex, transparent: true, depthWrite: false });
  return new THREE.Sprite(mat);
}

// --- Prelevements en eau (Hub'Eau) : palette dynamique par usage. La liste
// de categories n'est jamais figee en dur : on assigne une couleur de cette
// palette a chaque valeur distincte de `usage` rencontree dans les donnees.
// Palette volontairement saturee/vive : les barres doivent se detacher du
// terrain (verts/bruns) vu de loin, contrairement aux teintes plus douces
// utilisees ailleurs dans la scene (socle, villes...).
const USAGE_PALETTE = [
  "#ff5a3c", "#22d3ee", "#ffd23c", "#4ade80", "#e935e0", "#3b82f6", "#ff2f92", "#a3e635",
];

function formatVolume(v) {
  if (v == null) return "n/d";
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)} Mm³`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(0)} k m³`;
  return `${v.toFixed(0)} m³`;
}

// Repositionne une instance (position derivee de l'exageration + visibilite
// selon le filtre d'usage actif). Utilise a la construction et a chaque
// changement d'exageration/de filtre -> jamais de rebuild de geometrie.
// La barre (cylindre) est posee sur le terrain, base au sol, hauteur = donnee
// (pas affectee par l'exageration verticale, qui ne deplace que sa base).
// `radius` est parametrable : la barre visible est fine, mais la zone de
// clic (mesh invisible separe) est nettement plus large pour rester cliquable.
function applyWithdrawalTransform(mesh, dummy, meta, exaggeration, radius) {
  meta.currentY = meta.rawH * exaggeration;
  const height = meta.hiddenByFilter ? 0 : meta.baseHeight;
  const r = meta.hiddenByFilter ? 0 : radius;
  dummy.position.set(meta.x, meta.currentY + height / 2, meta.z);
  dummy.scale.set(r, height, r);
  dummy.updateMatrix();
  mesh.setMatrixAt(meta.index, dummy.matrix);
}

// Recalcule baseHeight/baseRadius/hitRadius de chaque site a partir des
// SEULES categories actives (activeUsages) : le 90e percentile qui plafonne
// l'echelle est recalcule sur les donnees reellement visibles, pour que le
// plus gros site affiche toujours une barre proche du maximum, meme apres
// avoir filtre les usages a plus gros volumes (ex: ne garder qu'IRRIGATION).
function recomputeWithdrawalScales(withdrawalMeta, activeUsages, worldDiag, zRange, widthScale, heightScale) {
  const visible = withdrawalMeta.filter((m) => !activeUsages || activeUsages.has(m.data.usage));
  const sortedVolumes = visible.map((m) => m.data.total_volume || 0).sort((a, b) => a - b);
  const domainMax = sortedVolumes.length
    ? sortedVolumes[Math.floor(0.9 * (sortedVolumes.length - 1))] || 1
    : 1;

  const minHeight = Math.max(worldDiag * 0.0009, 90) * heightScale;
  const maxHeight = Math.max(zRange * 0.55, 550) * heightScale;
  const barRadius = Math.max(worldDiag * 0.0011, 140) * widthScale;
  const hitRadius = Math.max(barRadius * 3, worldDiag * 0.001);

  withdrawalMeta.forEach((meta) => {
    const t = Math.sqrt(Math.min(meta.data.total_volume || 0, domainMax) / domainMax);
    meta.baseHeight = minHeight + (maxHeight - minHeight) * t;
    meta.baseRadius = barRadius;
    meta.hitRadius = hitRadius;
  });
}

function buildWallPositions(wallEdges, exaggeration, socleDepthRaw) {
  const positions = new Float32Array(wallEdges.length * 18); // 6 sommets * 3
  const baseY = -socleDepthRaw * exaggeration;
  for (let i = 0; i < wallEdges.length; i++) {
    const e = wallEdges[i];
    const ay = e.rawHa * exaggeration;
    const by = e.rawHb * exaggeration;
    const o = i * 18;
    // triangle 1 : A(haut) - B(haut) - A(bas)
    positions[o] = e.ax; positions[o + 1] = ay; positions[o + 2] = e.az;
    positions[o + 3] = e.bx; positions[o + 4] = by; positions[o + 5] = e.bz;
    positions[o + 6] = e.ax; positions[o + 7] = baseY; positions[o + 8] = e.az;
    // triangle 2 : B(haut) - B(bas) - A(bas)
    positions[o + 9] = e.bx; positions[o + 10] = by; positions[o + 11] = e.bz;
    positions[o + 12] = e.bx; positions[o + 13] = baseY; positions[o + 14] = e.bz;
    positions[o + 15] = e.ax; positions[o + 16] = baseY; positions[o + 17] = e.az;
  }
  return positions;
}

// --- FORET (PROTOTYPE) : positionne le tronc + le houppier d'un arbre a
// partir de sa meta (position/echelle/hauteurs precalculees), sur le meme
// principe que applyWithdrawalTransform : reutilise a la fois a la
// construction initiale et a chaque changement d'exageration verticale
// (seule la base bouge, les hauteurs des arbres restent des valeurs
// "reelles" non exagerees).
function applyForestTransform(trunkMesh, canopyMesh, dummy, t, i, exaggeration) {
  const baseY = t.rawH * exaggeration;
  dummy.position.set(t.x, baseY + t.trunkHeight / 2, t.z);
  dummy.scale.set(t.trunkRadius, t.trunkHeight, t.trunkRadius);
  dummy.rotation.y = t.rotationY;
  dummy.updateMatrix();
  trunkMesh.setMatrixAt(i, dummy.matrix);

  dummy.position.set(t.x, baseY + t.trunkHeight + t.canopyHeight / 2, t.z);
  dummy.scale.set(t.canopyRadius, t.canopyHeight, t.canopyRadius);
  dummy.updateMatrix();
  canopyMesh.setMatrixAt(i, dummy.matrix);
}

/**
 * Legende d'un indicateur, quelle que soit sa couche (communes, geologie,
 * debit) et quelle que soit sa nature.
 *
 * Un seul composant pour les quatre `kind` : c'est la contrepartie directe du
 * moteur descriptif — la legende se deduit de la specification, jamais des
 * couleurs affichees a l'ecran, donc les deux ne peuvent pas diverger.
 */
function IndicatorLegend({ indicator, legend, subtitle, note }) {
  if (!indicator || !legend) return null;
  return (
    <>
      <span className="terrain3d-usagelegend-title">
        {indicator.label}
        {subtitle && <span className="terrain3d-legend-subtitle"> — {subtitle}</span>}
      </span>
      {legend.kind === "quantitative" ? (
        <>
          <div className="terrain3d-legend-gradient" style={{ background: legend.gradient }} />
          <div className="terrain3d-legend-labels">
            <span>{formatNumber(legend.min, legend.format)} {legend.unit}</span>
            {/* Une echelle divergente doit dire ou tombe son point neutre,
                sinon le milieu de la rampe passe pour une mediane. */}
            {legend.neutral != null && <span className="terrain3d-legend-neutral">0</span>}
            <span>{formatNumber(legend.max, legend.format)} {legend.unit}</span>
          </div>
          {legend.scale === "log" && (
            <span className="terrain3d-commune-source">Échelle logarithmique</span>
          )}
        </>
      ) : (
        <div className="terrain3d-legend-chips">
          {legend.entries.map(({ value, label, color, count }) => (
            <span key={String(value)} className="terrain3d-geology-chip" style={{ "--chip-color": color }}>
              <span className="terrain3d-usage-dot" />
              {label}
              {count != null && ` (${count})`}
            </span>
          ))}
        </div>
      )}
      {note && <span className="terrain3d-commune-source">{note}</span>}
      {indicator.source && <span className="terrain3d-commune-source">{indicator.source}</span>}
    </>
  );
}

export default function TerrainTestPage() {
  const mountRef = useRef(null);
  const stateRef = useRef({});
  const [status, setStatus] = useState("loading"); // loading | error | ready
  const [exaggeration, setExaggeration] = useState(2.5);
  const [wireframe, setWireframe] = useState(false);
  const [autoRotate, setAutoRotate] = useState(true);
  const [fogEnabled, setFogEnabled] = useState(true);
  const [showRivers, setShowRivers] = useState(true);
  const [riverWidthScale, setRiverWidthScale] = useState(1);
  const [riverLengthCapKm, setRiverLengthCapKm] = useState(null); // longueur (km) a partir de laquelle un cours d'eau atteint l'epaisseur max
  const [riverLengthBounds, setRiverLengthBounds] = useState(null); // { min, max } observes dans le jeu de donnees charge, pour calibrer le slider
  const [showRiverNames, setShowRiverNames] = useState(true);
  const [hoveredRiver, setHoveredRiver] = useState(null); // { name, length_km, clientX, clientY }
  const [showCities, setShowCities] = useState(true);
  const [showWithdrawals, setShowWithdrawals] = useState(true);
  const [showRainfall, setShowRainfall] = useState(true);
  const [showGeology, setShowGeology] = useState(true);
  const [showForest, setShowForest] = useState(true); // PROTOTYPE : zone de test, pas une vraie couche d'occupation du sol
  // Dioramas : habillage 3D local (bourg, vignoble, cereales). Les pastilles
  // sont visibles d'emblee, le decor lui-meme n'est construit qu'au clic.
  const [showDioramaPins, setShowDioramaPins] = useState(true);
  const [dioramaSpots, setDioramaSpots] = useState([]);
  const [activeDioramaId, setActiveDioramaId] = useState(null);
  // Eteindre les couches calibrees pour la vue large pendant un gros plan.
  // Reglable : c'est un choix de mise en scene, pas une contrainte technique.
  const [dioramaCloseUp, setDioramaCloseUp] = useState(true);
  const [showCommunes, setShowCommunes] = useState(false); // eteinte par defaut : une nappe opaque de plus masquerait les autres calques
  const [showCommuneBorders, setShowCommuneBorders] = useState(true);
  const [communeOpacity, setCommuneOpacity] = useState(COMMUNE_DEFAULT_OPACITY);
  const [communes, setCommunes] = useState([]);
  const [communeIndicators, setCommuneIndicators] = useState([]); // specifications lues dans communes.json
  const [communeIndicatorId, setCommuneIndicatorId] = useState(null);
  const [hoveredCommune, setHoveredCommune] = useState(null); // { commune, clientX, clientY }
  const [selectedCommune, setSelectedCommune] = useState(null);
  const [showClimate, setShowClimate] = useState(false); // eteinte par defaut : encore une nappe qui masquerait les autres
  const [showClimateStations, setShowClimateStations] = useState(true);
  const [climatePoints, setClimatePoints] = useState([]);
  const [climateAxes, setClimateAxes] = useState({ variables: [], horizons: [], periods: [] });
  const [climateVariableId, setClimateVariableId] = useState(null);
  const [climateHorizon, setClimateHorizon] = useState(null);
  const [climatePeriod, setClimatePeriod] = useState("annuel");
  const [climateOpacity, setClimateOpacity] = useState(CLIMATE_DEFAULT_OPACITY);
  const [selectedStation, setSelectedStation] = useState(null);
  // Debit : une station par cours d'eau instrumente, pas de nappe interpolee
  // (un debit ne se lit qu'au point ou il est mesure, cf. dischargeLayer.js).
  const [showDischarge, setShowDischarge] = useState(false);
  const [dischargeStations, setDischargeStations] = useState([]);
  const [dischargeAxes, setDischargeAxes] = useState({ horizons: [], metrics: [] });
  const [dischargeMetricId, setDischargeMetricId] = useState(null);
  const [dischargeHorizon, setDischargeHorizon] = useState(null);
  const [dischargeNormalized, setDischargeNormalized] = useState(false);
  const [selectedDischarge, setSelectedDischarge] = useState(null);
  const [meta, setMeta] = useState(null);
  const [coveragePct, setCoveragePct] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null); // un "site" (regroupe >=1 ouvrage colocalise)
  const [focusedOuvrageIndex, setFocusedOuvrageIndex] = useState(0); // quel ouvrage du site est detaille/gaphe
  const [usageList, setUsageList] = useState([]); // [{usage, color}] construit depuis les donnees
  const [geologyIndicators, setGeologyIndicators] = useState([]); // specifications lues dans geology.json
  const [geologyIndicatorId, setGeologyIndicatorId] = useState(null);
  const [geologyOpacity, setGeologyOpacity] = useState(GEOLOGY_DEFAULT_OPACITY);
  const [hoveredGeology, setHoveredGeology] = useState(null); // { entity, clientX, clientY }
  const [activeUsages, setActiveUsages] = useState(null); // Set<string> | null (= tout actif)
  const [barWidthScale, setBarWidthScale] = useState(1);
  const [barHeightScale, setBarHeightScale] = useState(1.35);

  // --- Chargement des donnees + init three.js (une seule fois) ---
  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const [metaRes, binRes] = await Promise.all([
          fetch(`${DATA_URL}/meta.json`),
          fetch(`${DATA_URL}/heightmap.bin`),
        ]);
        if (!metaRes.ok || !binRes.ok) throw new Error("fichiers introuvables");
        const metaJson = await metaRes.json();
        const buffer = await binRes.arrayBuffer();
        if (cancelled) return;
        const heights = new Float32Array(buffer);

        let validCount = 0;
        for (let i = 0; i < heights.length; i++) if (heights[i] !== metaJson.nodata) validCount++;
        setCoveragePct((100 * validCount) / heights.length);

        // Calques optionnels : leur absence ne doit pas empecher le terrain de
        // s'afficher (fetch best-effort, chacun independant). Ils sont
        // regroupes dans un seul objet plutot que passes un a un : le
        // datapack en compte desormais huit, et une liste positionnelle
        // devenait aussi longue qu'illisible.
        const json = (name, fallback) =>
          fetch(`${DATA_URL}/${name}`)
            .then((r) => (r.ok ? r.json() : fallback))
            .catch(() => fallback);
        const raster = (name) =>
          fetch(`${DATA_URL}/${name}`)
            .then((r) => (r.ok ? r.arrayBuffer() : null))
            .then((buf) => (buf ? new Uint16Array(buf) : null))
            .catch(() => null);

        const [
          riversData, citiesData, withdrawalsData,
          geologyCodes, geologyData, communeCodes, communesData,
          climateData, dischargeData,
        ] = await Promise.all([
          json("rivers.json", []),
          json("cities.json", []),
          json("withdrawals.json", []),
          // Uint16 depuis la refonte : le raster porte un index d'ENTITE
          // BD LISA (64 entites ici, mais jusqu'a plusieurs centaines
          // ailleurs) et non plus une classe de couleur sur un octet.
          raster("geology.bin"),
          json("geology.json", null),
          raster("communes.bin"),
          json("communes.json", null),
          json("climate.json", null),
          json("discharge.json", null),
        ]);
        if (cancelled) return;

        setMeta(metaJson);
        initScene(metaJson, heights, {
          riversData, citiesData, withdrawalsData,
          geologyCodes, geologyData, communeCodes, communesData,
          climateData, dischargeData,
        });
        setStatus("ready");
      } catch (e) {
        console.error("Terrain test: echec chargement", e);
        if (!cancelled) setStatus("error");
      }
    }

    function initScene(metaJson, heights, data) {
      const {
        riversData, citiesData, withdrawalsData,
        geologyCodes, geologyData, communeCodes, communesData,
        climateData, dischargeData,
      } = data;
      const communeRecords = communesData?.communes || [];
      const geologyRecords = geologyData?.entities || [];
      const climatePointList = climateData?.points || [];
      const dischargeList = dischargeData?.stations || [];
      const { width: cols, height: rows, cellsize, origin_x: originX, origin_y: originY, zmin, zmax, nodata } = metaJson;
      const mount = mountRef.current;

      const zRange = zmax - zmin || 1;
      const halfW = ((cols - 1) * cellsize) / 2;
      const halfH = ((rows - 1) * cellsize) / 2;
      const worldW = cols * cellsize;
      const worldH = rows * cellsize;
      const socleDepthRaw = zRange * SOCLE_DEPTH_RATIO;
      const initialExaggeration = exaggeration;
      const initialBarWidthScale = barWidthScale;
      const initialBarHeightScale = barHeightScale;

      // Toutes les distances (fog, camera, plans near/far) sont derivees de la
      // diagonale du terrain plutot que de constantes fixes, pour que la scene
      // se cadre correctement que l'on affiche 4 tuiles ou tout le departement.
      const worldDiag = Math.sqrt(worldW * worldW + worldH * worldH);

      const scene = new THREE.Scene();
      const skyColor = new THREE.Color("#8fb7c9");
      const fogObj = new THREE.FogExp2(skyColor.getHex(), 0.9 / Math.max(worldW, worldH));
      scene.fog = fogEnabled ? fogObj : null;

      const farPlane = Math.max(200000, worldDiag * 3);
      const camera = new THREE.PerspectiveCamera(45, mount.clientWidth / mount.clientHeight, 10, farPlane);
      // Vue aerienne 3/4 par defaut (façon carte strategique), assez reculee
      // pour cadrer tout le territoire ET son socle des le chargement, quelle
      // que soit sa taille (angle constant, distance ~ diagonale).
      const camElevation = THREE.MathUtils.degToRad(42);
      const camAzimuth = THREE.MathUtils.degToRad(30.5);
      const camDist = worldDiag * 0.77;
      camera.position.set(
        camDist * Math.cos(camElevation) * Math.sin(camAzimuth),
        camDist * Math.sin(camElevation),
        camDist * Math.cos(camElevation) * Math.cos(camAzimuth)
      );

      const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.setSize(mount.clientWidth, mount.clientHeight);
      renderer.shadowMap.enabled = true;
      renderer.shadowMap.type = THREE.PCFSoftShadowMap;
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      mount.appendChild(renderer.domElement);

      const controls = new OrbitControls(camera, renderer.domElement);
      controls.target.set(0, 0, 0);
      controls.enableDamping = true;
      controls.dampingFactor = 0.08;
      controls.minDistance = 2000;
      controls.maxDistance = worldDiag * 1.4;
      controls.maxPolarAngle = Math.PI * 0.49;
      controls.autoRotate = autoRotate;
      controls.autoRotateSpeed = 0.35;
      controls.update();

      // --- masque de validite par sommet / par "quad" (case entre 4 sommets) ---
      const isValid = (r, c) => heights[r * cols + c] !== nodata;
      const quadValid = (r, c) =>
        isValid(r, c) && isValid(r, c + 1) && isValid(r + 1, c) && isValid(r + 1, c + 1);

      const quadValidMask = new Uint8Array((rows - 1) * (cols - 1));
      for (let r = 0; r < rows - 1; r++) {
        for (let c = 0; c < cols - 1; c++) {
          quadValidMask[r * (cols - 1) + c] = quadValid(r, c) ? 1 : 0;
        }
      }

      // --- terrain : sommets + couleurs + index (uniquement les quads valides) ---
      const positions = new Float32Array(cols * rows * 3);
      const colors = new Float32Array(cols * rows * 3);

      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const idx = r * cols + c;
          const h = heights[idx];
          const t = (h - zmin) / zRange;
          positions[idx * 3] = c * cellsize - halfW;
          positions[idx * 3 + 1] = (h - zmin) * initialExaggeration;
          positions[idx * 3 + 2] = r * cellsize - halfH;
          const [cr, cg, cb] = rampColor(t, ELEVATION_RAMP);
          colors[idx * 3] = cr;
          colors[idx * 3 + 1] = cg;
          colors[idx * 3 + 2] = cb;
        }
      }

      const terrainIndices = [];
      for (let r = 0; r < rows - 1; r++) {
        for (let c = 0; c < cols - 1; c++) {
          if (!quadValidMask[r * (cols - 1) + c]) continue;
          const a = r * cols + c;
          const b = a + 1;
          const d = a + cols;
          const e = d + 1;
          terrainIndices.push(a, d, b, b, d, e);
        }
      }

      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
      geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
      geometry.setIndex(terrainIndices);
      geometry.computeVertexNormals();

      const material = new THREE.MeshStandardMaterial({
        vertexColors: true,
        roughness: 0.92,
        metalness: 0.02,
        wireframe,
      });
      const terrain = new THREE.Mesh(geometry, material);
      terrain.receiveShadow = true;
      terrain.castShadow = true;
      scene.add(terrain);

      // --- pluviometrie (TEST, donnees synthetiques) : nappe de couleur
      // drapee sur le relief, en multiply-blend (blanc = neutre/transparent,
      // teinte = intensite de pluie) -> pas besoin d'alpha par sommet.
      // Le fog natif de three.js (scene.fog) est un brouillard de PROFONDEUR
      // (distance a la camera), il ne peut pas coder une valeur geographique
      // fixe -> on drape a la place une nappe sur le maillage du terrain.
      // Correle ici a l'altitude + un gradient ouest/est (les reliefs
      // cevenols a l'ouest recoivent plus de pluie que la vallee du Rhone a
      // l'est, coherent avec le climat reel de l'Ardeche) : donnees
      // fabriquees pour le test, a remplacer par une vraie grille meteo
      // (Meteo-France SAFRAN/AROME) en suivant le meme pipeline que le MNT.
      const rainfallColors = new Float32Array(cols * rows * 3);
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const idx = r * cols + c;
          const elevNorm = (heights[idx] - zmin) / zRange;
          const westEastNorm = c / (cols - 1);
          const rainNorm = Math.min(1, Math.max(0, 0.6 * elevNorm + 0.4 * (1 - westEastNorm)));
          const [cr, cg, cb] = rampColor(rainNorm, RAINFALL_RAMP);
          rainfallColors[idx * 3] = cr;
          rainfallColors[idx * 3 + 1] = cg;
          rainfallColors[idx * 3 + 2] = cb;
        }
      }
      const rainfallPositions = positions.slice();
      for (let i = 1; i < rainfallPositions.length; i += 3) rainfallPositions[i] += 25; // decollage anti z-fighting
      const rainfallGeometry = new THREE.BufferGeometry();
      rainfallGeometry.setAttribute("position", new THREE.BufferAttribute(rainfallPositions, 3));
      rainfallGeometry.setAttribute("color", new THREE.BufferAttribute(rainfallColors, 3));
      rainfallGeometry.setIndex(terrainIndices);
      const rainfallMaterial = new THREE.MeshBasicMaterial({
        vertexColors: true,
        transparent: true,
        opacity: 0.55,
        depthWrite: false,
        // Meme maillage que le terrain avec juste un leger decalage -> sans
        // polygonOffset, la precision du depth buffer a la distance ou l'on
        // voit le territoire entier cause un scintillement (z-fighting),
        // surtout visible sur les pentes. polygonOffset force le GPU a
        // rendre cette nappe legerement "devant" de facon fiable.
        polygonOffset: true,
        polygonOffsetFactor: -4,
        polygonOffsetUnits: -4,
      });
      const rainfallMesh = new THREE.Mesh(rainfallGeometry, rainfallMaterial);
      rainfallMesh.visible = showRainfall;
      scene.add(rainfallMesh);

      // --- geologie (BD LISA, BRGM) : nappe drapee sur le maillage terrain,
      // couleur par sommet, lue dans un raster precalcule cote Python
      // (build_overlays.py::build_geology) aligne cellule a cellule sur cette
      // meme grille -> simple lookup ici, aucun test point-in-polygon dans le
      // navigateur.
      //
      // Le raster porte un index d'ENTITE et non une classe de couleur : les
      // quatre classements de BD LISA (theme, nature, milieu, etat) sont donc
      // quatre colorations d'un meme fichier, exactement comme les communes.
      // Changer de classement ne recharge rien.
      let geologyMesh = null;
      let geologyGeometry = null;
      let geologyBaseColors = null;
      if (geologyCodes && geologyCodes.length === cols * rows && geologyRecords.length) {
        const { spec, colors } = resolveIndicator(
          geologyData.indicators, geologyRecords, geologyData.default_indicator
        );
        geologyBaseColors = colors;

        const geologyColors = new Float32Array(cols * rows * 3);
        paintRasterColors(geologyColors, geologyCodes, geologyBaseColors);
        const geologyPositions = positions.slice();
        for (let i = 1; i < geologyPositions.length; i += 3) geologyPositions[i] += GEOLOGY_Y_OFFSET;
        geologyGeometry = new THREE.BufferGeometry();
        geologyGeometry.setAttribute("position", new THREE.BufferAttribute(geologyPositions, 3));
        geologyGeometry.setAttribute("color", new THREE.BufferAttribute(geologyColors, 3));
        geologyGeometry.setIndex(terrainIndices);
        const geologyMaterial = new THREE.MeshBasicMaterial({
          vertexColors: true,
          transparent: true,
          opacity: geologyOpacity,
          depthWrite: false,
          polygonOffset: true,
          polygonOffsetFactor: -5,
          polygonOffsetUnits: -5,
        });
        geologyMesh = new THREE.Mesh(geologyGeometry, geologyMaterial);
        geologyMesh.visible = showGeology;
        scene.add(geologyMesh);

        setGeologyIndicators(geologyData.indicators || []);
        setGeologyIndicatorId(spec?.id || null);
      }

      // --- communes (INSEE / Agreste / arretes secheresse) : meme technique
      // de nappe drapee que la geologie, mais le raster ne code que
      // l'IDENTITE de la commune. Toutes les donnees communales tiennent
      // dans communes.json et c'est le front qui decide de l'indicateur
      // cartographie -> changer d'indicateur ne recharge rien et ne
      // reconstruit aucune geometrie, seulement les couleurs par sommet.
      // Le trace des limites est deduit du raster (transitions entre deux
      // cellules voisines), donc aucun fichier de contours a produire. ---
      let communeMesh = null;
      let communeGeometry = null;
      let communeBorderMesh = null;
      let communeHighlightMesh = null;
      let communeBorders = null;
      let communeCells = null;
      let communeBaseColors = null;
      if (communeCodes && communeCodes.length === cols * rows && communeRecords.length) {
        const { spec, colors } = resolveIndicator(
          communesData.indicators, communeRecords, communesData.default_indicator
        );
        communeBaseColors = colors;

        const communeColorArray = new Float32Array(cols * rows * 3);
        paintRasterColors(communeColorArray, communeCodes, communeBaseColors);
        const communePositions = positions.slice();
        for (let i = 1; i < communePositions.length; i += 3) communePositions[i] += COMMUNE_CHOROPLETH_Y_OFFSET;
        communeGeometry = new THREE.BufferGeometry();
        communeGeometry.setAttribute("position", new THREE.BufferAttribute(communePositions, 3));
        communeGeometry.setAttribute("color", new THREE.BufferAttribute(communeColorArray, 3));
        communeGeometry.setIndex(terrainIndices);
        const communeMaterial = new THREE.MeshBasicMaterial({
          vertexColors: true,
          transparent: true,
          opacity: communeOpacity,
          depthWrite: false,
          polygonOffset: true,
          polygonOffsetFactor: -6,
          polygonOffsetUnits: -6,
        });
        communeMesh = new THREE.Mesh(communeGeometry, communeMaterial);
        communeMesh.visible = showCommunes;
        scene.add(communeMesh);

        communeCells = buildCommuneCellIndex(communeCodes);

        // Une seule geometrie de limites, dont les segments sont ranges par
        // commune : le surlignage du survol partage ses attributs et n'en
        // dessine qu'une tranche (setDrawRange), sans rien reconstruire.
        communeBorders = buildCommuneBorders({ codes: communeCodes, heights, cols, rows, cellsize, zmin, nodata });
        const borderGeometry = new THREE.BufferGeometry();
        borderGeometry.setAttribute("position", new THREE.BufferAttribute(communeBorders.positions, 3));
        applyDrapedExaggeration(borderGeometry, communeBorders.rawH, initialExaggeration, COMMUNE_BORDER_Y_OFFSET);
        communeBorderMesh = new THREE.LineSegments(
          borderGeometry,
          new THREE.LineBasicMaterial({ color: "#1d3038", transparent: true, opacity: 0.42, depthWrite: false })
        );
        communeBorderMesh.visible = showCommunes && showCommuneBorders;
        scene.add(communeBorderMesh);

        const highlightGeometry = new THREE.BufferGeometry();
        highlightGeometry.setAttribute("position", borderGeometry.attributes.position);
        highlightGeometry.setDrawRange(0, 0);
        communeHighlightMesh = new THREE.LineSegments(
          highlightGeometry,
          new THREE.LineBasicMaterial({ color: "#ffffff", transparent: true, opacity: 0.95, depthWrite: false })
        );
        communeHighlightMesh.visible = showCommunes;
        scene.add(communeHighlightMesh);

        setCommunes(communeRecords);
        setCommuneIndicators(communesData.indicators || []);
        setCommuneIndicatorId(spec?.id || null);
      }

      // --- nappe d'eau : uniquement les quads valides ET bas (fond de vallee) ---
      const waterLevel = zmin + zRange * 0.035;
      const waterPositions = new Float32Array(cols * rows * 3);
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const idx = r * cols + c;
          waterPositions[idx * 3] = c * cellsize - halfW;
          waterPositions[idx * 3 + 1] = 0;
          waterPositions[idx * 3 + 2] = r * cellsize - halfH;
        }
      }
      const waterIndices = [];
      for (let r = 0; r < rows - 1; r++) {
        for (let c = 0; c < cols - 1; c++) {
          if (!quadValidMask[r * (cols - 1) + c]) continue;
          if (
            heights[r * cols + c] > waterLevel ||
            heights[r * cols + c + 1] > waterLevel ||
            heights[(r + 1) * cols + c] > waterLevel ||
            heights[(r + 1) * cols + c + 1] > waterLevel
          )
            continue;
          const a = r * cols + c;
          const b = a + 1;
          const d = a + cols;
          const e = d + 1;
          waterIndices.push(a, d, b, b, d, e);
        }
      }
      let water = null;
      if (waterIndices.length) {
        const waterGeo = new THREE.BufferGeometry();
        waterGeo.setAttribute("position", new THREE.BufferAttribute(waterPositions, 3));
        waterGeo.setIndex(waterIndices);
        waterGeo.computeVertexNormals();
        const waterMat = new THREE.MeshStandardMaterial({
          color: new THREE.Color("#54a8ba"),
          transparent: true,
          opacity: 0.75,
          roughness: 0.2,
          metalness: 0.1,
        });
        water = new THREE.Mesh(waterGeo, waterMat);
        water.position.y = (waterLevel - zmin) * initialExaggeration;
        scene.add(water);
      }

      // --- socle : murs le long du contour decoupe + dalle de base ---
      // (evite l'effet "feuille flottante" et de voir sous la carte)
      const wallEdges = [];
      const addEdge = (r0, c0, r1, c1) => {
        const i0 = r0 * cols + c0;
        const i1 = r1 * cols + c1;
        wallEdges.push({
          ax: c0 * cellsize - halfW,
          az: r0 * cellsize - halfH,
          rawHa: heights[i0] - zmin,
          bx: c1 * cellsize - halfW,
          bz: r1 * cellsize - halfH,
          rawHb: heights[i1] - zmin,
        });
      };
      const nQCols = cols - 1;
      for (let r = 0; r < rows - 1; r++) {
        for (let c = 0; c < nQCols; c++) {
          if (!quadValidMask[r * nQCols + c]) continue;
          const upValid = r > 0 && quadValidMask[(r - 1) * nQCols + c];
          const downValid = r < rows - 2 && quadValidMask[(r + 1) * nQCols + c];
          const leftValid = c > 0 && quadValidMask[r * nQCols + c - 1];
          const rightValid = c < nQCols - 1 && quadValidMask[r * nQCols + c + 1];
          if (!upValid) addEdge(r, c, r, c + 1); // arete haute
          if (!downValid) addEdge(r + 1, c + 1, r + 1, c); // arete basse
          if (!leftValid) addEdge(r + 1, c, r, c); // arete gauche
          if (!rightValid) addEdge(r, c + 1, r + 1, c + 1); // arete droite
        }
      }

      let wallMesh = null;
      if (wallEdges.length) {
        const wallColors = new Float32Array(wallEdges.length * 18);
        const top = lerpColor(WALL_TOP_COLOR, WALL_BOTTOM_COLOR, 0);
        const bot = lerpColor(WALL_TOP_COLOR, WALL_BOTTOM_COLOR, 1);
        for (let i = 0; i < wallEdges.length; i++) {
          const o = i * 18;
          const set = (off, col) => {
            wallColors[off] = col[0];
            wallColors[off + 1] = col[1];
            wallColors[off + 2] = col[2];
          };
          set(o, top); set(o + 3, top); set(o + 6, bot);
          set(o + 9, top); set(o + 12, bot); set(o + 15, bot);
        }
        const wallGeo = new THREE.BufferGeometry();
        wallGeo.setAttribute(
          "position",
          new THREE.BufferAttribute(buildWallPositions(wallEdges, initialExaggeration, socleDepthRaw), 3)
        );
        wallGeo.setAttribute("color", new THREE.BufferAttribute(wallColors, 3));
        wallGeo.computeVertexNormals();
        const wallMat = new THREE.MeshStandardMaterial({
          vertexColors: true,
          roughness: 1,
          metalness: 0,
          side: THREE.DoubleSide,
        });
        wallMesh = new THREE.Mesh(wallGeo, wallMat);
        wallMesh.receiveShadow = true;
        scene.add(wallMesh);
      }

      // --- lumieres ---
      const ambient = new THREE.HemisphereLight("#cfe8f0", "#4a3d2c", 0.9);
      scene.add(ambient);

      const sun = new THREE.DirectionalLight("#fff3d6", 1.35);
      sun.position.set(worldW * 0.4, Math.max(worldW, worldH) * 0.6, -worldH * 0.25);
      sun.castShadow = true;
      sun.shadow.mapSize.set(2048, 2048);
      sun.shadow.camera.left = -halfW * 1.1;
      sun.shadow.camera.right = halfW * 1.1;
      sun.shadow.camera.top = halfH * 1.1;
      sun.shadow.camera.bottom = -halfH * 1.1;
      sun.shadow.camera.near = 10;
      sun.shadow.camera.far = Math.max(worldW, worldH) * 2;
      sun.shadow.bias = -0.0015;
      scene.add(sun);

      // --- echantillonnage terrain (utilise par rivieres + villes) ---
      const northY = originY + rows * cellsize;
      const sampleAt = (px, py) => {
        const fc = (px - originX) / cellsize;
        const fr = (northY - py) / cellsize;
        const c0 = Math.floor(fc);
        const r0 = Math.floor(fr);
        if (c0 < 0 || r0 < 0 || c0 >= cols - 1 || r0 >= rows - 1) return null;
        const h00 = heights[r0 * cols + c0];
        const h10 = heights[r0 * cols + c0 + 1];
        const h01 = heights[(r0 + 1) * cols + c0];
        const h11 = heights[(r0 + 1) * cols + c0 + 1];
        if (h00 === nodata || h10 === nodata || h01 === nodata || h11 === nodata) return null;
        const fx = fc - c0;
        const fy = fr - r0;
        const h = h00 * (1 - fx) * (1 - fy) + h10 * fx * (1 - fy) + h01 * (1 - fx) * fy + h11 * fx * fy;
        return { x: fc * cellsize - halfW, z: fr * cellsize - halfH, rawH: h - zmin };
      };

      // Variante de sampleAt en coordonnees LOCALES (x/z deja centres sur le
      // terrain, pas Lambert93) : utile pour semer des elements directement
      // dans l'espace de la scene (ex: foret ci-dessous) sans repasser par
      // l'origine reelle.
      const sampleLocalHeight = (x, z) => {
        const fc = (x + halfW) / cellsize;
        const fr = (z + halfH) / cellsize;
        const c0 = Math.floor(fc);
        const r0 = Math.floor(fr);
        if (c0 < 0 || r0 < 0 || c0 >= cols - 1 || r0 >= rows - 1) return null;
        const h00 = heights[r0 * cols + c0];
        const h10 = heights[r0 * cols + c0 + 1];
        const h01 = heights[(r0 + 1) * cols + c0];
        const h11 = heights[(r0 + 1) * cols + c0 + 1];
        if (h00 === nodata || h10 === nodata || h01 === nodata || h11 === nodata) return null;
        const fx = fc - c0;
        const fy = fr - r0;
        const h = h00 * (1 - fx) * (1 - fy) + h10 * fx * (1 - fy) + h01 * (1 - fx) * fy + h11 * fx * fy;
        return h - zmin;
      };

      // Hauteur de la surface REELLEMENT RENDUE en un point local, par
      // interpolation dans le triangle qui le contient.
      //
      // sampleLocalHeight ci-dessus interpole de facon bilineaire, ce qui est
      // sans consequence pour un marqueur haut de plusieurs dizaines de
      // metres mais devient visible pour un objet de 1,5 m : sur une maille
      // de 200 m en forte pente, l'ecart entre la nappe bilineaire et les
      // deux triangles qui la rendent atteint plusieurs metres, multiplies
      // ensuite par l'exageration verticale. Les rangs de vigne flottaient
      // au-dessus du sol ou s'y enfoncaient.
      //
      // Le maillage coupe chaque quad selon la diagonale b-d (cf.
      // terrainIndices : a,d,b puis b,d,e), soit la droite fx + fy = 1 dans
      // les coordonnees fractionnaires de la maille.
      const sampleSurfaceHeight = (x, z) => {
        const fc = (x + halfW) / cellsize;
        const fr = (z + halfH) / cellsize;
        const c0 = Math.floor(fc);
        const r0 = Math.floor(fr);
        if (c0 < 0 || r0 < 0 || c0 >= cols - 1 || r0 >= rows - 1) return null;
        const h00 = heights[r0 * cols + c0];
        const h10 = heights[r0 * cols + c0 + 1];
        const h01 = heights[(r0 + 1) * cols + c0];
        const h11 = heights[(r0 + 1) * cols + c0 + 1];
        if (h00 === nodata || h10 === nodata || h01 === nodata || h11 === nodata) return null;
        const fx = fc - c0;
        const fy = fr - r0;
        const h =
          fx + fy <= 1
            ? h00 + fx * (h10 - h00) + fy * (h01 - h00)
            : h11 + (1 - fx) * (h01 - h11) + (1 - fy) * (h10 - h11);
        return h - zmin;
      };

      // --- rivieres : un ruban drape par polyligne valide, coupe aux trous ---
      const riversGroup = new THREE.Group();
      const riverRibbons = []; // { geometry, rawH, points, normals, river, currentHalfWidth }
      // Largeur du ruban proportionnelle a la longueur du cours d'eau
      // (longueur_km, transmise par build_overlays.py) : un cours d'eau de
      // longueur >= riverLengthCapKm atteint riverMaxHalfWidth, les autres
      // sont a l'echelle en-dessous. riverLengthCapKm est un reglage
      // utilisateur (slider "Longueur de reference"), pas une valeur figee :
      // ce qui compte comme "grand cours d'eau" depend du territoire et de
      // ce qu'on veut mettre en avant (ex: le Rhone ne fait qu'effleurer le
      // territoire mais porte sa longueur reelle de fleuve national -> on
      // veut pouvoir le faire ressortir sans ecraser les autres, ou au
      // contraire relever le seuil pour ne mettre en avant que lui seul).
      const riverMaxHalfWidth = Math.max(worldDiag * 0.0045, 350);
      const riverMinHalfWidth = Math.max(worldDiag * 0.00015, 12); // garde-fou anti-ruban degenere (longueur nulle/absente)
      const riverLengths = (riversData || []).map((r) => r.length_km || 0);
      const riverLengthDatasetMin = riverLengths.length ? Math.min(...riverLengths) : 1;
      const riverLengthDatasetMax = riverLengths.length ? Math.max(...riverLengths) : 1;
      const initialRiverLengthCapKm = riverLengthDatasetMax; // proportionnalite stricte par defaut, ajustable ensuite
      setRiverLengthBounds({ min: riverLengthDatasetMin, max: riverLengthDatasetMax });
      setRiverLengthCapKm(initialRiverLengthCapKm);
      const riverHalfWidthFor = (lengthKm, lengthCapKm) =>
        Math.max(riverMinHalfWidth, (riverMaxHalfWidth / lengthCapKm) * Math.min(lengthKm || 0, lengthCapKm));
      const riverBandLength = Math.max(worldDiag * 0.01, 300);
      const riverTexture = createFlowTexture();
      const riverMaterial = new THREE.MeshBasicMaterial({
        color: new THREE.Color(RIVER_COLOR),
        map: riverTexture,
        transparent: true,
        opacity: 0.9,
        side: THREE.DoubleSide,
        depthWrite: false,
      });
      const initialRiverWidthScale = riverWidthScale;
      (riversData || []).forEach((river) => {
        const halfWidth = riverHalfWidthFor(river.length_km, initialRiverLengthCapKm) * initialRiverWidthScale;
        (river.polylines || []).forEach((line) => {
          let run = [];
          const flush = () => {
            if (run.length >= 2) {
              const normals = computeRiverNormals(run);
              const { geometry, rawH } = buildRiverRibbon(run, normals, halfWidth, riverBandLength, initialExaggeration);
              const mesh = new THREE.Mesh(geometry, riverMaterial);
              riversGroup.add(mesh);
              riverRibbons.push({
                geometry,
                rawH,
                points: run,
                normals,
                currentHalfWidth: halfWidth,
                river: { name: river.name, length_km: river.length_km },
              });
            }
            run = [];
          };
          line.forEach(([x, y]) => {
            const s = sampleAt(x, y);
            if (s) run.push(s);
            else flush();
          });
          flush();
        });
      });
      riversGroup.visible = showRivers;
      scene.add(riversGroup);

      // --- villes : halo + faisceau + etiquette, interactifs (survol/clic) ---
      const citiesGroup = new THREE.Group();
      const cityMarkers = [];
      const cityHitMeshes = [];
      (citiesData || []).forEach((city) => {
        const sample = sampleAt(city.x, city.y);
        if (!sample) return;
        const style = CITY_TIERS[classifyCity(city)];

        const group = new THREE.Group();
        group.position.set(sample.x, sample.rawH * initialExaggeration, sample.z);

        const ringRadius = Math.max(worldDiag * 0.0055, 350) * style.ring;
        const ringGeo = new THREE.RingGeometry(ringRadius * 0.72, ringRadius, 40);
        ringGeo.rotateX(-Math.PI / 2);
        const ringMat = new THREE.MeshBasicMaterial({
          color: style.color,
          transparent: true,
          opacity: 0.85,
          side: THREE.DoubleSide,
          depthWrite: false,
        });
        const ring = new THREE.Mesh(ringGeo, ringMat);
        ring.position.y = 5;
        group.add(ring);

        const beamHeight = zRange * 0.55 * style.beam;
        const beamGeo = new THREE.CylinderGeometry(ringRadius * 0.1, ringRadius * 0.22, beamHeight, 14, 1, true);
        const beamMat = new THREE.MeshBasicMaterial({
          color: style.color,
          transparent: true,
          opacity: 0.3,
          side: THREE.DoubleSide,
          depthWrite: false,
          blending: THREE.AdditiveBlending,
        });
        const beam = new THREE.Mesh(beamGeo, beamMat);
        beam.position.y = beamHeight / 2;
        group.add(beam);

        const label = createLabelSprite(city.name, style.color);
        const labelScale = Math.max(worldDiag * 0.045, 2800);
        label.scale.set(labelScale, labelScale * 0.28, 1);
        label.position.y = beamHeight + labelScale * 0.22;
        group.add(label);

        const hitGeo = new THREE.CylinderGeometry(ringRadius * 1.5, ringRadius * 1.5, beamHeight + ringRadius, 10);
        const hitMesh = new THREE.Mesh(hitGeo, new THREE.MeshBasicMaterial({ visible: false }));
        hitMesh.position.y = beamHeight / 2;
        group.add(hitMesh);

        citiesGroup.add(group);

        const marker = { group, ring, beam, label, hitMesh, rawH: sample.rawH, data: city };
        cityMarkers.push(marker);
        cityHitMeshes.push(hitMesh);
        hitMesh.userData.markerIndex = cityMarkers.length - 1;
      });
      citiesGroup.visible = showCities;
      scene.add(citiesGroup);

      // --- prelevements en eau (Hub'Eau) : InstancedMesh, pas des Mesh
      // individuels (jusqu'a plusieurs milliers de points -> un Mesh par
      // point saturerait les draw calls, contrairement aux 5 villes) ---
      let withdrawalMesh = null;
      let withdrawalHitMesh = null;
      const withdrawalMeta = [];
      const withdrawalDummy = new THREE.Object3D();
      if (withdrawalsData && withdrawalsData.length) {
        const validPoints = [];
        withdrawalsData.forEach((o) => {
          const sample = sampleAt(o.x, o.y);
          if (sample) validPoints.push({ o, sample });
        });

        const distinctUsages = Array.from(new Set(validPoints.map((p) => p.o.usage))).sort();
        const usageColorMap = new Map(
          distinctUsages.map((u, i) => [u, new THREE.Color(USAGE_PALETTE[i % USAGE_PALETTE.length])])
        );

        const barGeo = new THREE.CylinderGeometry(1, 1, 1, 8);
        const withdrawalMat = new THREE.MeshStandardMaterial({ roughness: 0.35, metalness: 0 });
        withdrawalMesh = new THREE.InstancedMesh(barGeo, withdrawalMat, validPoints.length);
        withdrawalMesh.castShadow = true;

        // Zone de clic separee, invisible, nettement plus large que la barre
        // visible : un baton de quelques dizaines de metres de large est
        // quasi impossible a viser precisement une fois affiche a l'echelle
        // d'un departement.
        withdrawalHitMesh = new THREE.InstancedMesh(
          barGeo,
          new THREE.MeshBasicMaterial({ visible: false }),
          validPoints.length
        );

        validPoints.forEach(({ o, sample }, i) => {
          const meta = {
            index: i,
            data: o,
            rawH: sample.rawH,
            x: sample.x,
            z: sample.z,
            baseRadius: 0,
            hitRadius: 0,
            baseHeight: 0,
            baseColor: usageColorMap.get(o.usage) || new THREE.Color("#ffffff"),
            hiddenByFilter: false,
            currentY: 0,
          };
          withdrawalMesh.setColorAt(i, meta.baseColor);
          withdrawalMeta.push(meta);
        });
        // Barres verticales : seule la HAUTEUR encode le volume (echelle
        // liee au relief lui-meme, pas a l'exageration -> stable quand on
        // bouge le slider). Plafond sur le 90e percentile REEL des donnees
        // (les usages "energie"/"eau turbinee" ecraseraient sinon l'echelle,
        // avec des volumes ~1000x superieurs a l'eau potable/irrigation).
        recomputeWithdrawalScales(withdrawalMeta, null, worldDiag, zRange, initialBarWidthScale, initialBarHeightScale);
        withdrawalMeta.forEach((meta) => {
          applyWithdrawalTransform(withdrawalMesh, withdrawalDummy, meta, initialExaggeration, meta.baseRadius);
          applyWithdrawalTransform(withdrawalHitMesh, withdrawalDummy, meta, initialExaggeration, meta.hitRadius);
        });
        withdrawalMesh.instanceMatrix.needsUpdate = true;
        if (withdrawalMesh.instanceColor) withdrawalMesh.instanceColor.needsUpdate = true;
        withdrawalHitMesh.instanceMatrix.needsUpdate = true;
        withdrawalMesh.visible = showWithdrawals;
        scene.add(withdrawalMesh);
        scene.add(withdrawalHitMesh);

        setUsageList(distinctUsages.map((u) => ({ usage: u, color: `#${usageColorMap.get(u).getHexString()}` })));
      }

      // --- FORET (PROTOTYPE) : arbres generiques (tronc + houppier, formes
      // simples) semes ALEATOIREMENT dans un disque de test au centre du
      // terrain, juste pour valider le rendu (densite, lisibilite depuis la
      // camera par defaut, cout du InstancedMesh) avant d'investir dans un
      // vrai calque d'occupation du sol (a produire plus tard sur le meme
      // pipeline que build_overlays.py::build_geology, puis a semer ici
      // uniquement dans les zones "foret"). Deux InstancedMesh (tronc,
      // houppier) partageant les memes transforms par instance : un seul
      // draw call par piece, que l'on ait 500 ou 50 000 arbres.
      const FOREST_ZONE_RADIUS = worldDiag * 0.14;
      const FOREST_COUNT = 3000;
      const forestMeta = []; // { x, z, rawH, scale, rotationY, trunkHeight, trunkRadius, canopyHeight, canopyRadius }
      const forestDummy = new THREE.Object3D();
      let forestTrunkMesh = null;
      let forestCanopyMesh = null;
      {
        const waterLevelRaw = waterLevel - zmin;
        const trunkHeightBase = Math.max(worldDiag * 0.0009, 70);
        const trunkRadiusBase = trunkHeightBase * 0.09;
        const canopyHeightBase = trunkHeightBase * 2.6;
        const canopyRadiusBase = trunkHeightBase * 1.1;

        // Semis par rejet dans un disque (repartition uniforme, pas de biais
        // vers le centre) : on tire large (jusqu'a 4x le compte cible) car
        // une partie des tirages tombe hors zone valide (nodata) ou dans
        // l'eau, et on s'arrete la des que le disque est rempli ou que le
        // budget de tentatives est epuise (pas de boucle infinie si la zone
        // choisie est en grande partie invalide).
        let attempts = 0;
        while (forestMeta.length < FOREST_COUNT && attempts < FOREST_COUNT * 4) {
          attempts++;
          const angle = Math.random() * Math.PI * 2;
          const r = Math.sqrt(Math.random()) * FOREST_ZONE_RADIUS;
          const x = Math.cos(angle) * r;
          const z = Math.sin(angle) * r;
          const rawH = sampleLocalHeight(x, z);
          if (rawH == null || rawH <= waterLevelRaw + 5) continue;
          const scale = 0.7 + Math.random() * 0.7;
          forestMeta.push({
            x,
            z,
            rawH,
            scale,
            rotationY: Math.random() * Math.PI * 2,
            trunkHeight: trunkHeightBase * scale,
            trunkRadius: trunkRadiusBase * scale,
            canopyHeight: canopyHeightBase * scale,
            canopyRadius: canopyRadiusBase * scale,
          });
        }

        if (forestMeta.length) {
          const trunkGeo = new THREE.CylinderGeometry(1, 1, 1, 6);
          const trunkMat = new THREE.MeshStandardMaterial({ color: "#6b4a34", roughness: 0.95 });
          forestTrunkMesh = new THREE.InstancedMesh(trunkGeo, trunkMat, forestMeta.length);
          forestTrunkMesh.castShadow = true;

          // Houppier : cone stylise (coherent avec le rendu "jeu video /
          // strategie" recherche sur le reste de la scene), teinte variee
          // par instance (instanceColor) pour eviter l'effet "copier-coller"
          // d'un vert unique repete des milliers de fois.
          const canopyGeo = new THREE.ConeGeometry(1, 1, 7);
          const canopyMat = new THREE.MeshStandardMaterial({ roughness: 0.85 });
          forestCanopyMesh = new THREE.InstancedMesh(canopyGeo, canopyMat, forestMeta.length);
          forestCanopyMesh.castShadow = true;
          forestCanopyMesh.receiveShadow = true;

          const canopyColorA = new THREE.Color("#3f7a3a");
          const canopyColorB = new THREE.Color("#6a9c47");

          forestMeta.forEach((t, i) => {
            applyForestTransform(forestTrunkMesh, forestCanopyMesh, forestDummy, t, i, initialExaggeration);
            forestCanopyMesh.setColorAt(i, canopyColorA.clone().lerp(canopyColorB, Math.random()));
          });

          forestTrunkMesh.instanceMatrix.needsUpdate = true;
          forestCanopyMesh.instanceMatrix.needsUpdate = true;
          if (forestCanopyMesh.instanceColor) forestCanopyMesh.instanceColor.needsUpdate = true;
          forestTrunkMesh.visible = showForest;
          forestCanopyMesh.visible = showForest;
          scene.add(forestTrunkMesh);
          scene.add(forestCanopyMesh);
        }
      }

      // --- DIORAMAS : habillage 3D LOCAL, a l'oppose d'une couche.
      //
      // Ces objets sont a l'echelle reelle (une maison fait 10 m, un rang de
      // vigne 1,5 m de haut) : ils ne veulent rien dire sur la vue large et
      // ne cherchent pas a en dire. Ils servent le moment ou l'animateur
      // zoome sur un endroit pour parler de la vigne ou du mais, et ou la
      // carte doit cesser d'etre une carte.
      //
      // Rien n'est construit au chargement. Une pastille marque chaque
      // emplacement ; le decor n'est bati qu'au premier clic, puis conserve.
      // Les couches existantes ne sont pas modifiees : un diorama s'ajoute
      // par-dessus et se retire sans laisser de trace.
      const dioramaSpotList = pickDioramaSpots({ communes: communeRecords, cities: citiesData });
      setDioramaSpots(dioramaSpotList);

      const dioramaPinsGroup = new THREE.Group();
      const dioramaPins = [];
      const dioramaPinHitMeshes = [];
      dioramaSpotList.forEach((spot) => {
        const pin = createDioramaPin(spot, { sampleAt, worldDiag, exaggeration: initialExaggeration });
        if (!pin) return;
        pin.hitMesh.userData.dioramaIndex = dioramaPins.length;
        dioramaPins.push(pin);
        dioramaPinHitMeshes.push(pin.hitMesh);
        dioramaPinsGroup.add(pin.group);
      });
      dioramaPinsGroup.visible = showDioramaPins;
      scene.add(dioramaPinsGroup);

      const dioramasBuilt = new Map(); // spot.id -> diorama construit (garde en cache)
      let activeDiorama = null;

      // Vol de camera : on ne TELEPORTE pas. Passer d'un coup de la vue
      // departementale a 300 m du sol fait perdre le fil a la salle — le
      // trajet, lui, montre ou l'on est en train de descendre. L'azimut
      // courant est conserve : on se rapproche sans faire pivoter la scene
      // sous les yeux du groupe.
      const overviewPosition = camera.position.clone();
      const flight = {
        active: false, t: 0, duration: 1.6, lastT: null,
        fromPos: new THREE.Vector3(), toPos: new THREE.Vector3(),
        fromTarget: new THREE.Vector3(), toTarget: new THREE.Vector3(),
      };
      const startFlight = (toTarget, distance, explicitPosition) => {
        flight.fromPos.copy(camera.position);
        flight.fromTarget.copy(controls.target);
        flight.toTarget.copy(toTarget);
        if (explicitPosition) {
          flight.toPos.copy(explicitPosition);
        } else {
          const dir = new THREE.Vector3().subVectors(camera.position, toTarget);
          dir.y = 0;
          if (dir.lengthSq() < 1) dir.set(0, 0, 1);
          dir.normalize();
          const elevation = THREE.MathUtils.degToRad(24);
          flight.toPos
            .copy(toTarget)
            .addScaledVector(dir, Math.cos(elevation) * distance)
            .addScaledVector(new THREE.Vector3(0, 1, 0), Math.sin(elevation) * distance);
        }
        flight.t = 0;
        flight.lastT = null;
        flight.active = true;
        // Les controles sont rendus pendant le vol : un drag en cours de
        // trajet se battrait avec l'interpolation.
        controls.enabled = false;
      };

      // MODE GROS PLAN. Toutes les couches existantes sont dimensionnees pour
      // la vue departementale, et c'est precisement ce qui les rend
      // inutilisables a 300 m du sol :
      //   - les nappes drapees (pluviometrie, geologie, communes, climat)
      //     flottent 25 m au-dessus du terrain — soit plus haut que les
      //     maisons, qu'elles recouvrent purement et simplement ;
      //   - les barres de prelevement, les marqueurs de ville et les pastilles
      //     de station tirent leur taille de la diagonale du territoire : a
      //     cette distance ce sont des tours de 100 a 400 m.
      // On les eteint donc le temps de la visite, et on RETABLIT l'etat exact
      // qu'elles avaient au retour : la vue d'ensemble est rendue telle qu'on
      // l'avait laissee. Le terrain, l'eau, les rivieres et la foret restent —
      // ils sont a l'echelle du monde, donc ils tiennent de pres.
      // Ombres du gros plan. La carte d'ombre de la scene fait 2048 texels
      // pour 100 km, soit 50 m par texel : elle ne peut rien dire d'un objet
      // de 10 m, et c'est pour ca que les dioramas ne projetaient rien. On ne
      // touche pas a la carte, on RECADRE sa camera orthographique sur le
      // diorama : 2048 texels pour 800 m, soit 40 cm par texel — de vraies
      // ombres de toit et d'inter-rang. Le cadrage large est retabli au
      // retour, sinon le territoire perdrait ses ombres de relief.
      const wideShadow = {
        left: sun.shadow.camera.left,
        right: sun.shadow.camera.right,
        top: sun.shadow.camera.top,
        bottom: sun.shadow.camera.bottom,
        near: sun.shadow.camera.near,
        far: sun.shadow.camera.far,
        targetPosition: sun.target.position.clone(),
        bias: sun.shadow.bias,
        normalBias: sun.shadow.normalBias,
      };
      // La cible d'une DirectionalLight n'agit que si elle appartient au
      // graphe de scene : sans ca, deplacer sun.target ne recentrerait rien.
      scene.add(sun.target);

      const focusShadowOn = (anchorPoint, radius) => {
        const span = radius * 1.35;
        sun.target.position.copy(anchorPoint);
        sun.target.updateMatrixWorld();
        const distance = sun.position.distanceTo(anchorPoint);
        sun.shadow.camera.left = -span;
        sun.shadow.camera.right = span;
        sun.shadow.camera.top = span;
        sun.shadow.camera.bottom = -span;
        sun.shadow.camera.near = Math.max(1, distance - span * 4);
        sun.shadow.camera.far = distance + span * 4;
        sun.shadow.camera.updateProjectionMatrix();
      };
      const restoreWideShadow = () => {
        sun.target.position.copy(wideShadow.targetPosition);
        sun.target.updateMatrixWorld();
        Object.assign(sun.shadow.camera, {
          left: wideShadow.left, right: wideShadow.right,
          top: wideShadow.top, bottom: wideShadow.bottom,
          near: wideShadow.near, far: wideShadow.far,
        });
        sun.shadow.camera.updateProjectionMatrix();
      };

      const closeUpHidden = [];
      const wideScaleObjects = () => [
        rainfallMesh, geologyMesh, communeMesh, communeBorderMesh, communeHighlightMesh,
        climateMesh, climateStations?.mesh, climateStations?.hitMesh,
        dischargeMarkers?.mesh, dischargeMarkers?.hitMesh,
        withdrawalMesh, withdrawalHitMesh, citiesGroup,
      ].filter(Boolean);
      const enterCloseUp = () => {
        if (closeUpHidden.length) return;
        wideScaleObjects().forEach((obj) => {
          closeUpHidden.push({ obj, visible: obj.visible });
          obj.visible = false;
        });
      };
      const exitCloseUp = () => {
        closeUpHidden.splice(0).forEach(({ obj, visible }) => {
          obj.visible = visible;
        });
      };

      const focusDiorama = (spotId) => {
        const spot = dioramaSpotList.find((s) => s.id === spotId);
        if (!spot) return;
        const ex = stateRef.current.exaggeration ?? initialExaggeration;

        let diorama = dioramasBuilt.get(spotId);
        if (!diorama) {
          diorama = buildDiorama(spot, {
            sampleAt,
            // Surface triangulee et non nappe bilineaire : a l'echelle d'un
            // rang de vigne, l'ecart entre les deux se voit.
            sampleLocalHeight: sampleSurfaceHeight,
            exaggeration: ex,
          });
          if (!diorama) return;
          dioramasBuilt.set(spotId, diorama);
          scene.add(diorama.group);
        }

        if (activeDiorama && activeDiorama !== diorama) activeDiorama.group.visible = false;
        diorama.group.visible = true;
        activeDiorama = diorama;
        if (stateRef.current.dioramaCloseUp !== false) enterCloseUp();
        if (diorama.shadows) {
          focusShadowOn(
            new THREE.Vector3(diorama.anchor.x, diorama.anchor.rawH * ex, diorama.anchor.z),
            diorama.radius
          );
        } else {
          restoreWideShadow();
        }

        // La distance minimale d'orbite vaut 2 km en vue large : elle
        // interdirait purement et simplement d'approcher un diorama de 400 m
        // de rayon. On la relache le temps de la visite, et on la retablit au
        // retour pour ne pas laisser la vue d'ensemble traversable.
        controls.minDistance = 120;
        setAutoRotate(false);
        setActiveDioramaId(spotId);
        startFlight(
          new THREE.Vector3(diorama.anchor.x, diorama.anchor.rawH * ex, diorama.anchor.z),
          diorama.frameDistance
        );
      };

      // Le curseur d'exageration deplace l'ancrage de plusieurs centaines de
      // metres en hauteur : sans ce recadrage, le diorama sortirait du
      // tronc de la camera d'ombre et perdrait ses ombres en cours de route.
      const refocusShadow = (ex) => {
        if (!activeDiorama?.shadows) return;
        focusShadowOn(
          new THREE.Vector3(activeDiorama.anchor.x, activeDiorama.anchor.rawH * ex, activeDiorama.anchor.z),
          activeDiorama.radius
        );
      };

      const clearDiorama = () => {
        // Sans diorama affiche il n'y a rien a defaire : ramener la camera
        // ici deplacerait la vue au seul motif qu'on a decoche une case.
        if (!activeDiorama) return;
        activeDiorama.group.visible = false;
        activeDiorama = null;
        exitCloseUp();
        restoreWideShadow();
        controls.minDistance = 2000;
        setActiveDioramaId(null);
        startFlight(new THREE.Vector3(0, 0, 0), 0, overviewPosition);
      };

      // Plan de reference (hauteur ~ niveau des rivieres) utilise pour projeter
      // le curseur au sol lors du survol des rivieres : evite un raycast
      // contre le maillage terrain complet (des centaines de milliers de
      // triangles, trop couteux a chaque mousemove) au prix d'une petite
      // approximation, acceptable puisque les rivieres se trouvent justement
      // pres de ce niveau bas.
      // --- climat : nappe interpolee depuis les points de mesure + les
      // points eux-memes en marqueurs. Rien n'est precalcule cote Python
      // (273 combinaisons variable/horizon/mois, cf. climateLayer.js) :
      // l'interpolation passe par une grille intermediaire dont les poids
      // sont calcules une fois, donc changer de mois coute quelques ms.
      let climateMesh = null;
      let climateGeometry = null;
      let climateInterpolator = null;
      let climateStations = null;
      if (climatePointList.length) {
        const axes = parseClimateAxes(climateData);
        if (axes.variables.length && axes.horizons.length) {
          const variable = axes.variables[0];
          const horizon = axes.horizons[0];
          const period = axes.periods.includes("annuel") ? "annuel" : axes.periods[0];
          const domain = climateDomain(climatePointList, variable, period, axes.horizons);

          climateInterpolator = createClimateInterpolator({
            points: climatePointList, cols, rows, cellsize, originX, originY,
          });

          const climateColorArray = new Float32Array(cols * rows * 3);
          climateInterpolator.fillColors(
            climateColorArray, climateValues(climatePointList, variable, horizon, period), domain, variable.ramp
          );
          const climatePositions = positions.slice();
          for (let i = 1; i < climatePositions.length; i += 3) climatePositions[i] += CLIMATE_Y_OFFSET;
          climateGeometry = new THREE.BufferGeometry();
          climateGeometry.setAttribute("position", new THREE.BufferAttribute(climatePositions, 3));
          climateGeometry.setAttribute("color", new THREE.BufferAttribute(climateColorArray, 3));
          climateGeometry.setIndex(terrainIndices);
          climateMesh = new THREE.Mesh(
            climateGeometry,
            new THREE.MeshBasicMaterial({
              vertexColors: true,
              transparent: true,
              opacity: climateOpacity,
              depthWrite: false,
              polygonOffset: true,
              polygonOffsetFactor: -7,
              polygonOffsetUnits: -7,
            })
          );
          climateMesh.visible = showClimate;
          scene.add(climateMesh);

          climateStations = createClimateStations({
            points: climatePointList, sampleAt, worldDiag, zRange, exaggeration: initialExaggeration,
          });
          if (climateStations) {
            paintClimateStations(climateStations, variable, horizon, period, domain);
            climateStations.mesh.visible = showClimate && showClimateStations;
            climateStations.hitMesh.visible = showClimate && showClimateStations;
            scene.add(climateStations.mesh);
            scene.add(climateStations.hitMesh);
          }

          setClimatePoints(climatePointList);
          setClimateAxes(axes);
          setClimateVariableId(variable.id);
          setClimateHorizon(horizon);
          setClimatePeriod(period);
        }
      }

      // --- debit : marqueurs de stations hydrometriques. Pas de nappe : un
      // debit est la propriete d'un cours d'eau en un point, interpoler entre
      // deux bassins versants ne voudrait rien dire (cf. dischargeLayer.js).
      // C'est la seule couche qui aille jusqu'a 2100.
      let dischargeMarkers = null;
      if (dischargeList.length) {
        const axes = parseDischargeAxes(dischargeData);
        if (axes.metrics.length && axes.horizons.length) {
          const metric = axes.metrics[0];
          const horizon = axes.horizons[0];
          dischargeMarkers = createDischargeStations({
            stations: dischargeList, sampleAt, worldDiag, zRange, exaggeration: initialExaggeration,
          });
          if (dischargeMarkers) {
            paintDischargeStations(dischargeMarkers, metric, horizon);
            dischargeMarkers.mesh.visible = showDischarge;
            dischargeMarkers.hitMesh.visible = showDischarge;
            scene.add(dischargeMarkers.mesh);
            scene.add(dischargeMarkers.hitMesh);
          }
          setDischargeStations(dischargeList);
          setDischargeAxes(axes);
          setDischargeMetricId(metric.id);
          setDischargeHorizon(horizon);
        }
      }

      const riverPickPlane = new THREE.Plane(
        new THREE.Vector3(0, 1, 0),
        -((waterLevel - zmin) * initialExaggeration)
      );

      // Point du relief vise par le curseur, obtenu en marchant le long du
      // rayon contre le modele d'altitude (cf. createTerrainPicker) : sert a
      // savoir sur quelle commune on pointe. L'astuce du plan horizontal
      // ci-dessus ne conviendrait pas ici — elle n'est juste qu'au voisinage
      // du niveau des rivieres, alors que les communes couvrent tout le
      // relief, de 40 m a 1 700 m.
      const pickTerrainCell = createTerrainPicker({ heights, cols, rows, cellsize, zmin, zmax, nodata });

      // A appeler apres `pick()`, qui a deja oriente `raycaster` sur le
      // curseur. Un seul sondage du relief sert les deux couches rasterisees :
      // elles partagent la grille du terrain, donc l'index de cellule.
      const cellAt = () => {
        if (!communeMesh?.visible && !geologyMesh?.visible) return null;
        return pickTerrainCell(raycaster.ray, stateRef.current.exaggeration ?? initialExaggeration);
      };
      const communeAtCell = (cell) => {
        if (!cell || !communeMesh || !communeMesh.visible) return -1;
        const id = communeCodes[cell.index];
        return id === COMMUNE_NODATA ? -1 : id;
      };
      const geologyAtCell = (cell) => {
        if (!cell || !geologyMesh || !geologyMesh.visible) return null;
        return geologyEntityAt(geologyCodes, geologyRecords, cell.index);
      };

      // Surlignage : la commune survolee est eclaircie (ses cellules) et son
      // contour ressort (tranche dediee de la geometrie des limites).
      const applyCommuneHover = (index) => {
        const s = stateRef.current;
        const previous = s.hoveredCommuneIndex ?? -1;
        if (index === previous) return;
        const colorAttribute = communeGeometry.attributes.color;
        const baseColors = s.communeBaseColors || communeBaseColors;
        if (previous >= 0) paintCells(colorAttribute.array, communeCells.get(previous), baseColors[previous]);
        if (index >= 0) paintCells(colorAttribute.array, communeCells.get(index), communeHoverColor(baseColors[index]));
        colorAttribute.needsUpdate = true;
        const range = index >= 0 ? communeBorders.ranges.get(index) : null;
        communeHighlightMesh.geometry.setDrawRange(range ? range.start : 0, range ? range.count : 0);
        s.hoveredCommuneIndex = index;
      };

      // --- survol / clic : villes + prelevements ---
      const raycaster = new THREE.Raycaster();
      const pointerNdc = new THREE.Vector2();
      let hoveredMarkerIndex = -1;
      let hoveredWithdrawalIndex = -1;

      const pick = (event) => {
        const rect = renderer.domElement.getBoundingClientRect();
        pointerNdc.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        pointerNdc.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
        raycaster.setFromCamera(pointerNdc, camera);

        const cityHits = raycaster.intersectObjects(cityHitMeshes, false);
        const withdrawalHits =
          withdrawalHitMesh && withdrawalMesh.visible ? raycaster.intersectObject(withdrawalHitMesh, false) : [];
        const climateHits =
          climateStations && climateStations.mesh.visible
            ? raycaster.intersectObject(climateStations.hitMesh, false)
            : [];
        const dischargeHits =
          dischargeMarkers && dischargeMarkers.mesh.visible
            ? raycaster.intersectObject(dischargeMarkers.hitMesh, false)
            : [];

        // Le marqueur le plus proche de la camera l'emporte, quel que soit
        // son type (une liste triee plutot qu'une cascade de comparaisons
        // deux a deux, qui ne passait pas l'echelle a trois familles).
        const candidates = [];
        if (cityHits.length) {
          candidates.push({ d: cityHits[0].distance, hit: { type: "city", index: cityHits[0].object.userData.markerIndex } });
        }
        if (withdrawalHits.length) {
          candidates.push({ d: withdrawalHits[0].distance, hit: { type: "withdrawal", index: withdrawalHits[0].instanceId } });
        }
        if (climateHits.length) {
          candidates.push({ d: climateHits[0].distance, hit: { type: "climate", index: climateHits[0].instanceId } });
        }
        if (dischargeHits.length) {
          candidates.push({ d: dischargeHits[0].distance, hit: { type: "discharge", index: dischargeHits[0].instanceId } });
        }
        const dioramaHits = dioramaPinsGroup.visible
          ? raycaster.intersectObjects(dioramaPinHitMeshes, false)
          : [];
        if (dioramaHits.length) {
          candidates.push({
            d: dioramaHits[0].distance,
            hit: { type: "diorama", index: dioramaHits[0].object.userData.dioramaIndex },
          });
        }
        if (!candidates.length) return null;
        candidates.sort((a, b) => a.d - b.d);
        return candidates[0].hit;
      };

      // Survol d'une station : la pastille grossit et blanchit, sans perdre
      // sa couleur de fond (qui code la valeur affichee).
      let hoveredStationIndex = -1;
      const setStationHover = (index, hovered) => {
        if (index < 0 || !climateStations) return;
        const station = climateStations.meta[index];
        applyClimateStationTransform(
          climateStations.mesh, climateStations.dummy, station,
          stateRef.current.exaggeration ?? initialExaggeration,
          hovered ? station.radius * 1.9 : station.radius
        );
        climateStations.mesh.setColorAt(
          index,
          hovered ? station.baseColor.clone().lerp(new THREE.Color("#ffffff"), 0.55) : station.baseColor
        );
        climateStations.mesh.instanceMatrix.needsUpdate = true;
        if (climateStations.mesh.instanceColor) climateStations.mesh.instanceColor.needsUpdate = true;
      };

      // Meme traitement que les stations climatiques : le marqueur grossit et
      // blanchit sans perdre sa couleur de fond, qui code la valeur affichee.
      let hoveredDischargeIndex = -1;
      const setDischargeHover = (index, hovered) => {
        if (index < 0 || !dischargeMarkers) return;
        const station = dischargeMarkers.meta[index];
        applyDischargeStationTransform(
          dischargeMarkers.mesh, dischargeMarkers.dummy, station,
          stateRef.current.exaggeration ?? initialExaggeration,
          hovered ? station.radius * 1.8 : station.radius
        );
        dischargeMarkers.mesh.setColorAt(
          index,
          hovered ? station.baseColor.clone().lerp(new THREE.Color("#ffffff"), 0.55) : station.baseColor
        );
        dischargeMarkers.mesh.instanceMatrix.needsUpdate = true;
        if (dischargeMarkers.mesh.instanceColor) dischargeMarkers.mesh.instanceColor.needsUpdate = true;
      };

      const setWithdrawalHover = (index, hovered) => {
        if (index < 0 || !withdrawalMesh) return;
        const meta = withdrawalMeta[index];
        // Le survol epaissit la barre (feedback d'interaction) mais ne
        // touche jamais a sa hauteur, qui encode une donnee reelle.
        const radius = hovered ? meta.baseRadius * 2 : meta.baseRadius;
        withdrawalDummy.position.set(meta.x, meta.currentY + meta.baseHeight / 2, meta.z);
        withdrawalDummy.scale.set(radius, meta.baseHeight, radius);
        withdrawalDummy.updateMatrix();
        withdrawalMesh.setMatrixAt(index, withdrawalDummy.matrix);
        withdrawalMesh.setColorAt(
          index,
          hovered ? meta.baseColor.clone().lerp(new THREE.Color("#ffffff"), 0.5) : meta.baseColor
        );
        withdrawalMesh.instanceMatrix.needsUpdate = true;
        if (withdrawalMesh.instanceColor) withdrawalMesh.instanceColor.needsUpdate = true;
      };

      let lastGroundHoverCheck = 0;
      const riverPlaneHit = new THREE.Vector3();
      const handlePointerMove = (event) => {
        const hit = pick(event); // renseigne aussi `raycaster` pour les survols au sol ci-dessous
        hoveredMarkerIndex = hit?.type === "city" ? hit.index : -1;
        const newWithdrawalIndex = hit?.type === "withdrawal" ? hit.index : -1;
        if (newWithdrawalIndex !== hoveredWithdrawalIndex) {
          setWithdrawalHover(hoveredWithdrawalIndex, false);
          setWithdrawalHover(newWithdrawalIndex, true);
          hoveredWithdrawalIndex = newWithdrawalIndex;
        }
        const newStationIndex = hit?.type === "climate" ? hit.index : -1;
        if (newStationIndex !== hoveredStationIndex) {
          setStationHover(hoveredStationIndex, false);
          setStationHover(newStationIndex, true);
          hoveredStationIndex = newStationIndex;
        }
        const newDischargeIndex = hit?.type === "discharge" ? hit.index : -1;
        if (newDischargeIndex !== hoveredDischargeIndex) {
          setDischargeHover(hoveredDischargeIndex, false);
          setDischargeHover(newDischargeIndex, true);
          hoveredDischargeIndex = newDischargeIndex;
        }
        renderer.domElement.style.cursor = hit ? "pointer" : "default";

        // Survols "au sol" (rivieres, communes) : throttle commun a ~20
        // verifications/s -- une recherche du segment le plus proche parmi
        // ~226k points, ou un sondage du relief, a chaque mousemove brut
        // (jusqu'a 100+/s) serait inutilement couteux.
        const now = performance.now();
        if (now - lastGroundHoverCheck < 50) return;
        lastGroundHoverCheck = now;

        if (riversGroup.visible && stateRef.current.showRiverNames) {
          const gotPlaneHit = raycaster.ray.intersectPlane(riverPickPlane, riverPlaneHit);
          const river = gotPlaneHit ? findHoveredRiver(riverRibbons, riverPlaneHit.x, riverPlaneHit.z, worldDiag) : null;
          setHoveredRiver(river ? { ...river, clientX: event.clientX, clientY: event.clientY } : null);
        } else {
          setHoveredRiver(null);
        }

        // Un marqueur sous le curseur l'emporte : sa propre infobulle suffit,
        // inutile d'y superposer celle de la commune qui est dessous.
        const cell = hit ? null : cellAt();
        const communeIndex = communeAtCell(cell);
        applyCommuneHover(communeIndex);
        setHoveredCommune(
          communeIndex >= 0
            ? { commune: communeRecords[communeIndex], clientX: event.clientX, clientY: event.clientY }
            : null
        );
        // L'infobulle geologique ne s'affiche que si aucune commune ne la
        // precede : les deux nappes se superposent, deux infobulles au meme
        // endroit seraient illisibles.
        const entity = communeIndex >= 0 ? null : geologyAtCell(cell);
        setHoveredGeology(
          entity ? { entity, clientX: event.clientX, clientY: event.clientY } : null
        );
      };
      const handlePointerLeave = () => {
        setHoveredRiver(null);
        applyCommuneHover(-1);
        setHoveredCommune(null);
        setHoveredGeology(null);
        setStationHover(hoveredStationIndex, false);
        hoveredStationIndex = -1;
        setDischargeHover(hoveredDischargeIndex, false);
        hoveredDischargeIndex = -1;
      };
      const handlePointerDown = (event) => {
        const hit = pick(event);
        // Une seule fiche ouverte a la fois : ouvrir celle du marqueur clique
        // ferme les autres, sinon deux panneaux se disputeraient l'ecran.
        const openOnly = (setter, value) => {
          [setSelectedCity, setSelectedWithdrawal, setSelectedStation,
           setSelectedDischarge, setSelectedCommune].forEach((fn) => {
            if (fn !== setter) fn(null);
          });
          if (setter) setter(value);
        };
        // Un diorama n'ouvre aucune fiche : il habille la zone et y emmene la
        // camera. Les fiches ouvertes sont refermees pour degager la vue.
        if (hit?.type === "diorama") {
          openOnly(null, null);
          return focusDiorama(dioramaPins[hit.index].spot.id);
        }
        if (hit?.type === "city") return openOnly(setSelectedCity, cityMarkers[hit.index].data);
        if (hit?.type === "withdrawal") return openOnly(setSelectedWithdrawal, withdrawalMeta[hit.index].data);
        if (hit?.type === "climate") return openOnly(setSelectedStation, climateStations.meta[hit.index].data);
        if (hit?.type === "discharge") return openOnly(setSelectedDischarge, dischargeMarkers.meta[hit.index].data);
        const communeIndex = communeAtCell(cellAt());
        openOnly(setSelectedCommune, communeIndex >= 0 ? communeRecords[communeIndex] : null);
      };
      renderer.domElement.addEventListener("pointermove", handlePointerMove);
      renderer.domElement.addEventListener("pointerdown", handlePointerDown);
      renderer.domElement.addEventListener("pointerleave", handlePointerLeave);

      let frameId;
      const clock = new THREE.Clock();
      const animate = () => {
        frameId = requestAnimationFrame(animate);
        const t = clock.getElapsedTime();
        const dt = clock.getDelta();

        riverTexture.offset.x = (riverTexture.offset.x - dt * 0.12) % 1;

        cityMarkers.forEach((m, i) => {
          const target = i === hoveredMarkerIndex ? 1.3 : 1;
          m.hoverScale = THREE.MathUtils.lerp(m.hoverScale ?? 1, target, 0.15);
          const pulse = 1 + Math.sin(t * 1.6 + i * 1.7) * 0.06;
          m.ring.scale.setScalar(m.hoverScale * pulse);
          m.ring.material.opacity = (i === hoveredMarkerIndex ? 1 : 0.75) * (0.85 + Math.sin(t * 1.6 + i * 1.7) * 0.1);
        });

        dioramaPins.forEach((pin, i) => {
          const isActive = activeDiorama?.spot.id === pin.spot.id;
          // La pastille du diorama affiche s'efface : elle recouvrirait le
          // decor qu'elle vient d'appeler.
          pin.group.visible = !isActive;
          const breathe = Math.sin(t * 1.9 + i * 2.1);
          pin.edge.material.opacity = 0.6 + breathe * 0.2;
          pin.pad.material.opacity = 0.22 + breathe * 0.07;
        });

        if (flight.active) {
          // Delta propre au vol : `dt` plus haut vaut ~0 (getElapsedTime()
          // consomme deja le delta de l'horloge), on repart donc du temps
          // ecoule plutot que de s'appuyer dessus.
          const previous = flight.lastT ?? t;
          flight.lastT = t;
          flight.t = Math.min(1, flight.t + (t - previous) / flight.duration);
          // Ease-in-out cubique : depart et arrivee amortis, donc un
          // mouvement qui se lit comme un deplacement et non comme un saut.
          const p = flight.t;
          const eased = p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2;
          camera.position.lerpVectors(flight.fromPos, flight.toPos, eased);
          controls.target.lerpVectors(flight.fromTarget, flight.toTarget, eased);
          if (flight.t >= 1) {
            flight.active = false;
            controls.enabled = true;
          }
        }

        controls.update();
        renderer.render(scene, camera);
      };
      animate();

      const handleResize = () => {
        if (!mount) return;
        camera.aspect = mount.clientWidth / mount.clientHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(mount.clientWidth, mount.clientHeight);
      };
      window.addEventListener("resize", handleResize);

      stateRef.current = {
        renderer,
        scene,
        camera,
        controls,
        terrain,
        geometry,
        material,
        rainfallMesh,
        rainfallGeometry,
        geologyMesh,
        geologyGeometry,
        geologyCodes,
        geologyRecords,
        geologyBaseColors,
        heights,
        zmin,
        zRange,
        worldDiag,
        cols,
        rows,
        frameId,
        handleResize,
        water,
        waterLevel,
        wallMesh,
        wallEdges,
        socleDepthRaw,
        fogObj,
        riversGroup,
        riverRibbons,
        riverMaterial,
        riverTexture,
        riverPickPlane,
        riverMaxHalfWidth,
        riverMinHalfWidth,
        citiesGroup,
        cityMarkers,
        withdrawalMesh,
        withdrawalHitMesh,
        withdrawalMeta,
        withdrawalDummy,
        forestTrunkMesh,
        forestCanopyMesh,
        forestMeta,
        forestDummy,
        dioramaPinsGroup,
        dioramaPins,
        dioramasBuilt,
        // Exposes pour que le panneau React puisse declencher la meme action
        // que le clic sur une pastille : un seul chemin de code, donc pas de
        // divergence possible entre les deux commandes.
        focusDiorama,
        clearDiorama,
        enterCloseUp,
        exitCloseUp,
        refocusShadow,
        // Meme raison que showRiverNames plus bas : cette assignation remplace
        // stateRef.current en entier, la valeur doit donc y figurer.
        dioramaCloseUp,
        communeMesh,
        communeGeometry,
        communeBorderMesh,
        communeHighlightMesh,
        communeCodes,
        communesData,
        communeRecords,
        communeCells,
        communeBorders,
        communeBaseColors,
        hoveredCommuneIndex: -1,
        applyCommuneHover,
        climateMesh,
        climateGeometry,
        climateInterpolator,
        climateStations,
        climatePointList,
        dischargeMarkers,
        dischargeList,
        // Exageration courante : relue par communeAt() pour sonder le relief
        // a la bonne hauteur (le slider vertical la fait varier en direct).
        exaggeration: initialExaggeration,
        handlePointerMove,
        handlePointerDown,
        handlePointerLeave,
        // Valeur au montage : cette assignation remplace entierement
        // stateRef.current, donc toute mise a jour posee par l'effet
        // [showRiverNames] avant que ce chargement (async) ne se termine
        // serait sinon perdue.
        showRiverNames,
      };
    }

    load();

    return () => {
      cancelled = true;
      const s = stateRef.current;
      if (s.frameId) cancelAnimationFrame(s.frameId);
      if (s.handleResize) window.removeEventListener("resize", s.handleResize);
      if (s.renderer && s.handlePointerMove) s.renderer.domElement.removeEventListener("pointermove", s.handlePointerMove);
      if (s.renderer && s.handlePointerDown) s.renderer.domElement.removeEventListener("pointerdown", s.handlePointerDown);
      if (s.renderer && s.handlePointerLeave) s.renderer.domElement.removeEventListener("pointerleave", s.handlePointerLeave);
      if (s.controls) s.controls.dispose();
      if (s.renderer) {
        s.renderer.dispose();
        if (mountRef.current && s.renderer.domElement.parentNode === mountRef.current) {
          mountRef.current.removeChild(s.renderer.domElement);
        }
      }
      [
        s.geometry, s.rainfallGeometry, s.geologyGeometry, s.water?.geometry, s.wallMesh?.geometry,
        s.communeGeometry, s.communeBorderMesh?.geometry, s.communeHighlightMesh?.geometry,
        s.climateGeometry, s.climateStations?.mesh.geometry,
        s.dischargeMarkers?.mesh.geometry,
      ].forEach((g) => g && g.dispose());
      [
        s.material, s.rainfallMesh?.material, s.geologyMesh?.material, s.water?.material, s.wallMesh?.material,
        s.communeMesh?.material, s.communeBorderMesh?.material, s.communeHighlightMesh?.material,
        s.climateMesh?.material, s.climateStations?.mesh.material, s.climateStations?.hitMesh.material,
        s.dischargeMarkers?.mesh.material, s.dischargeMarkers?.hitMesh.material,
      ].forEach((m) => m && m.dispose());
      (s.riverRibbons || []).forEach(({ geometry }) => geometry.dispose());
      if (s.riverMaterial) s.riverMaterial.dispose();
      if (s.riverTexture) s.riverTexture.dispose();
      (s.cityMarkers || []).forEach((m) => {
        m.ring.geometry.dispose();
        m.ring.material.dispose();
        m.beam.geometry.dispose();
        m.beam.material.dispose();
        m.hitMesh.geometry.dispose();
        m.hitMesh.material.dispose();
        m.label.material.map?.dispose();
        m.label.material.dispose();
      });
      if (s.withdrawalMesh) {
        s.withdrawalMesh.geometry.dispose();
        s.withdrawalMesh.material.dispose();
      }
      if (s.withdrawalHitMesh) s.withdrawalHitMesh.material.dispose();
      if (s.forestTrunkMesh) {
        s.forestTrunkMesh.geometry.dispose();
        s.forestTrunkMesh.material.dispose();
      }
      if (s.forestCanopyMesh) {
        s.forestCanopyMesh.geometry.dispose();
        s.forestCanopyMesh.material.dispose();
      }
      (s.dioramaPins || []).forEach(disposeDioramaPin);
      (s.dioramasBuilt ? Array.from(s.dioramasBuilt.values()) : []).forEach((d) => d.dispose());
      stateRef.current = {};
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- exageration verticale : recalcule terrain + murs + socle en direct ---
  useEffect(() => {
    const s = stateRef.current;
    if (!s.geometry || !s.heights) return;
    const { geometry, heights, cols, rows, zmin } = s;
    const pos = geometry.attributes.position;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const idx = r * cols + c;
        pos.setY(idx, (heights[idx] - zmin) * exaggeration);
      }
    }
    pos.needsUpdate = true;
    geometry.computeVertexNormals();

    if (s.rainfallGeometry) {
      const rainPos = s.rainfallGeometry.attributes.position;
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const idx = r * cols + c;
          rainPos.setY(idx, (heights[idx] - zmin) * exaggeration + 25);
        }
      }
      rainPos.needsUpdate = true;
    }

    if (s.geologyGeometry) {
      const geoPos = s.geologyGeometry.attributes.position;
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const idx = r * cols + c;
          geoPos.setY(idx, (heights[idx] - zmin) * exaggeration + GEOLOGY_Y_OFFSET);
        }
      }
      geoPos.needsUpdate = true;
    }

    if (s.communeGeometry) {
      const communePos = s.communeGeometry.attributes.position;
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const idx = r * cols + c;
          communePos.setY(idx, (heights[idx] - zmin) * exaggeration + COMMUNE_CHOROPLETH_Y_OFFSET);
        }
      }
      communePos.needsUpdate = true;
      applyDrapedExaggeration(
        s.communeBorderMesh.geometry, s.communeBorders.rawH, exaggeration, COMMUNE_BORDER_Y_OFFSET
      );
    }

    if (s.climateGeometry) {
      const climatePos = s.climateGeometry.attributes.position;
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const idx = r * cols + c;
          climatePos.setY(idx, (heights[idx] - zmin) * exaggeration + CLIMATE_Y_OFFSET);
        }
      }
      climatePos.needsUpdate = true;
    }
    if (s.climateStations) {
      s.climateStations.meta.forEach((station) => {
        applyClimateStationTransform(s.climateStations.mesh, s.climateStations.dummy, station, exaggeration, station.radius);
        applyClimateStationTransform(s.climateStations.hitMesh, s.climateStations.dummy, station, exaggeration, station.hitRadius);
      });
      s.climateStations.mesh.instanceMatrix.needsUpdate = true;
      s.climateStations.hitMesh.instanceMatrix.needsUpdate = true;
    }
    if (s.dischargeMarkers) {
      s.dischargeMarkers.meta.forEach((station) => {
        applyDischargeStationTransform(s.dischargeMarkers.mesh, s.dischargeMarkers.dummy, station, exaggeration, station.radius);
        applyDischargeStationTransform(s.dischargeMarkers.hitMesh, s.dischargeMarkers.dummy, station, exaggeration, station.hitRadius);
      });
      s.dischargeMarkers.mesh.instanceMatrix.needsUpdate = true;
      s.dischargeMarkers.hitMesh.instanceMatrix.needsUpdate = true;
    }
    s.exaggeration = exaggeration;

    if (s.water) s.water.position.y = (s.waterLevel - zmin) * exaggeration;
    if (s.riverPickPlane) s.riverPickPlane.constant = -((s.waterLevel - zmin) * exaggeration);

    if (s.wallMesh && s.wallEdges.length) {
      const newPositions = buildWallPositions(s.wallEdges, exaggeration, s.socleDepthRaw);
      s.wallMesh.geometry.attributes.position.array.set(newPositions);
      s.wallMesh.geometry.attributes.position.needsUpdate = true;
      s.wallMesh.geometry.computeVertexNormals();
    }

    (s.riverRibbons || []).forEach(({ geometry, rawH }) => applyRiverExaggeration(geometry, rawH, exaggeration));
    (s.cityMarkers || []).forEach((m) => {
      m.group.position.y = m.rawH * exaggeration;
    });

    if (s.withdrawalMesh && s.withdrawalMeta.length) {
      s.withdrawalMeta.forEach((meta) => {
        applyWithdrawalTransform(s.withdrawalMesh, s.withdrawalDummy, meta, exaggeration, meta.baseRadius);
        applyWithdrawalTransform(s.withdrawalHitMesh, s.withdrawalDummy, meta, exaggeration, meta.hitRadius);
      });
      s.withdrawalMesh.instanceMatrix.needsUpdate = true;
      s.withdrawalHitMesh.instanceMatrix.needsUpdate = true;
    }

    if (s.forestTrunkMesh && s.forestMeta.length) {
      s.forestMeta.forEach((t, i) =>
        applyForestTransform(s.forestTrunkMesh, s.forestCanopyMesh, s.forestDummy, t, i, exaggeration)
      );
      s.forestTrunkMesh.instanceMatrix.needsUpdate = true;
      s.forestCanopyMesh.instanceMatrix.needsUpdate = true;
    }

    // Dioramas : on recale TOUS ceux deja construits, y compris ceux qui sont
    // masques. Ne recaler que le diorama visible laisserait les autres a
    // l'ancienne exageration, et ils apparaitraient enfonces dans le relief
    // ou suspendus au-dessus lors du prochain affichage.
    (s.dioramaPins || []).forEach((pin) => {
      pin.group.position.y = pin.rawH * exaggeration;
    });
    if (s.dioramasBuilt) {
      s.dioramasBuilt.forEach((diorama) => diorama.applyExaggeration(exaggeration));
    }
    s.refocusShadow?.(exaggeration);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exaggeration]);

  useEffect(() => {
    const s = stateRef.current;
    if (s.material) s.material.wireframe = wireframe;
  }, [wireframe]);

  useEffect(() => {
    const s = stateRef.current;
    if (s.controls) s.controls.autoRotate = autoRotate;
  }, [autoRotate]);

  useEffect(() => {
    const s = stateRef.current;
    if (s.scene) s.scene.fog = fogEnabled ? s.fogObj : null;
  }, [fogEnabled]);

  useEffect(() => {
    const s = stateRef.current;
    if (s.riversGroup) s.riversGroup.visible = showRivers;
  }, [showRivers]);

  useEffect(() => {
    const s = stateRef.current;
    if (!s.riverRibbons || !s.riverRibbons.length || !riverLengthCapKm) return;
    const widthPerKm = s.riverMaxHalfWidth / riverLengthCapKm;
    s.riverRibbons.forEach((ribbon) => {
      const lengthKm = ribbon.river?.length_km || 0;
      const raw = Math.max(s.riverMinHalfWidth, widthPerKm * Math.min(lengthKm, riverLengthCapKm));
      const halfWidth = raw * riverWidthScale;
      ribbon.currentHalfWidth = halfWidth;
      applyRiverWidth(ribbon.geometry, ribbon.points, ribbon.normals, halfWidth);
    });
  }, [riverWidthScale, riverLengthCapKm]);

  useEffect(() => {
    stateRef.current.showRiverNames = showRiverNames;
    if (!showRiverNames) setHoveredRiver(null);
  }, [showRiverNames]);

  useEffect(() => {
    const s = stateRef.current;
    if (s.citiesGroup) s.citiesGroup.visible = showCities;
  }, [showCities]);

  useEffect(() => {
    const s = stateRef.current;
    if (s.rainfallMesh) s.rainfallMesh.visible = showRainfall;
  }, [showRainfall]);

  useEffect(() => {
    const s = stateRef.current;
    if (!s.geologyMesh) return;
    s.geologyMesh.visible = showGeology;
    if (!showGeology) setHoveredGeology(null);
  }, [showGeology]);

  useEffect(() => {
    const s = stateRef.current;
    if (s.geologyMesh) s.geologyMesh.material.opacity = geologyOpacity;
  }, [geologyOpacity]);

  // --- classement geologique : recolore la nappe depuis le meme raster
  // d'entites. Quatre lectures du sous-sol, un seul fichier, aucune
  // geometrie reconstruite. ---
  useEffect(() => {
    const s = stateRef.current;
    if (!s.geologyMesh || !s.geologyRecords?.length || !geologyIndicatorId) return;
    const { colors } = resolveIndicator(geologyIndicators, s.geologyRecords, geologyIndicatorId);
    s.geologyBaseColors = colors;
    const colorAttribute = s.geologyGeometry.attributes.color;
    paintRasterColors(colorAttribute.array, s.geologyCodes, colors);
    colorAttribute.needsUpdate = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geologyIndicatorId]);

  useEffect(() => {
    const s = stateRef.current;
    if (!s.dischargeMarkers) return;
    s.dischargeMarkers.mesh.visible = showDischarge;
    s.dischargeMarkers.hitMesh.visible = showDischarge;
    if (!showDischarge) setSelectedDischarge(null);
  }, [showDischarge]);

  // --- grandeur / horizon de debit : recolore les marqueurs. L'echelle est
  // commune aux quatre horizons (bornes calculees par le pipeline), sinon
  // l'evolution jusqu'a 2100 serait invisible. ---
  useEffect(() => {
    const s = stateRef.current;
    if (!s.dischargeMarkers || !dischargeMetricId || !dischargeHorizon) return;
    const metric = dischargeAxes.metrics.find((m) => m.id === dischargeMetricId);
    if (metric) paintDischargeStations(s.dischargeMarkers, metric, dischargeHorizon);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dischargeMetricId, dischargeHorizon]);

  useEffect(() => {
    const s = stateRef.current;
    if (s.forestTrunkMesh) s.forestTrunkMesh.visible = showForest;
    if (s.forestCanopyMesh) s.forestCanopyMesh.visible = showForest;
  }, [showForest]);

  // Masquer les pastilles retire aussi le decor en cours : laisser un village
  // sans son point d'appel donnerait un habillage qu'on ne saurait plus
  // eteindre.
  useEffect(() => {
    const s = stateRef.current;
    if (!s.dioramaPinsGroup) return;
    s.dioramaPinsGroup.visible = showDioramaPins;
    if (!showDioramaPins && s.clearDiorama) s.clearDiorama();
  }, [showDioramaPins]);

  // Le reglage s'applique a chaud : on peut rallumer les couches larges sans
  // quitter le gros plan, ce qui est exactement le geste utile pour montrer
  // « la geologie sous ce village-la ».
  useEffect(() => {
    const s = stateRef.current;
    s.dioramaCloseUp = dioramaCloseUp;
    if (!s.enterCloseUp || !activeDioramaId) return;
    if (dioramaCloseUp) s.enterCloseUp();
    else s.exitCloseUp();
  }, [dioramaCloseUp, activeDioramaId]);

  useEffect(() => {
    const s = stateRef.current;
    if (s.withdrawalMesh) s.withdrawalMesh.visible = showWithdrawals;
  }, [showWithdrawals]);

  useEffect(() => {
    const s = stateRef.current;
    if (!s.communeMesh) return;
    s.communeMesh.visible = showCommunes;
    s.communeBorderMesh.visible = showCommunes && showCommuneBorders;
    s.communeHighlightMesh.visible = showCommunes;
    // Eteindre la couche doit aussi effacer ce qu'elle avait surligne/ouvert.
    if (!showCommunes) {
      s.applyCommuneHover(-1);
      setHoveredCommune(null);
      setSelectedCommune(null);
    }
  }, [showCommunes, showCommuneBorders]);

  useEffect(() => {
    const s = stateRef.current;
    if (s.communeMesh) s.communeMesh.material.opacity = communeOpacity;
  }, [communeOpacity]);

  useEffect(() => {
    const s = stateRef.current;
    if (!s.climateMesh) return;
    s.climateMesh.visible = showClimate;
    if (s.climateStations) {
      s.climateStations.mesh.visible = showClimate && showClimateStations;
      s.climateStations.hitMesh.visible = showClimate && showClimateStations;
    }
    if (!showClimate) setSelectedStation(null);
  }, [showClimate, showClimateStations]);

  useEffect(() => {
    const s = stateRef.current;
    if (s.climateMesh) s.climateMesh.material.opacity = climateOpacity;
  }, [climateOpacity]);

  // --- variable / horizon / periode climatique : reinterpole la nappe et
  // recolore les stations. Les poids d'interpolation etant deja calcules
  // (createClimateInterpolator), il ne reste qu'une somme ponderee sur la
  // grille intermediaire puis un echantillonnage vers les sommets. ---
  useEffect(() => {
    const s = stateRef.current;
    if (!s.climateMesh || !s.climateInterpolator || !climateVariableId || !climateHorizon) return;
    const variable = climateAxes.variables.find((v) => v.id === climateVariableId);
    if (!variable) return;
    const domain = climateDomain(s.climatePointList, variable, climatePeriod, climateAxes.horizons);
    const values = climateValues(s.climatePointList, variable, climateHorizon, climatePeriod);
    const colorAttribute = s.climateGeometry.attributes.color;
    s.climateInterpolator.fillColors(colorAttribute.array, values, domain, variable.ramp);
    colorAttribute.needsUpdate = true;
    if (s.climateStations) paintClimateStations(s.climateStations, variable, climateHorizon, climatePeriod, domain);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [climateVariableId, climateHorizon, climatePeriod]);

  // --- indicateur communal : recolore la nappe a partir du meme raster
  // (aucune geometrie reconstruite, aucun fichier retelecharge) ---
  useEffect(() => {
    const s = stateRef.current;
    if (!s.communeMesh || !s.communeRecords?.length || !communeIndicatorId) return;
    const { colors } = resolveIndicator(communeIndicators, s.communeRecords, communeIndicatorId);
    s.applyCommuneHover(-1); // sinon la teinte de survol serait figee dans les nouvelles couleurs
    s.communeBaseColors = colors;
    const colorAttribute = s.communeGeometry.attributes.color;
    paintRasterColors(colorAttribute.array, s.communeCodes, colors);
    colorAttribute.needsUpdate = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [communeIndicatorId]);

  // --- filtre par usage + reglages largeur/hauteur : recalcule l'echelle de
  // hauteur sur les SEULES categories visibles (le plus gros site filtre
  // atteint toujours ~le maximum, plutot que de rester ecrase par l'echelle
  // du jeu de donnees complet) et masque les instances desactivees ---
  useEffect(() => {
    const s = stateRef.current;
    if (!s.withdrawalMesh || !s.withdrawalMeta.length) return;
    recomputeWithdrawalScales(s.withdrawalMeta, activeUsages, s.worldDiag, s.zRange, barWidthScale, barHeightScale);
    s.withdrawalMeta.forEach((meta) => {
      meta.hiddenByFilter = activeUsages ? !activeUsages.has(meta.data.usage) : false;
      applyWithdrawalTransform(s.withdrawalMesh, s.withdrawalDummy, meta, exaggeration, meta.baseRadius);
      applyWithdrawalTransform(s.withdrawalHitMesh, s.withdrawalDummy, meta, exaggeration, meta.hitRadius);
    });
    s.withdrawalMesh.instanceMatrix.needsUpdate = true;
    s.withdrawalHitMesh.instanceMatrix.needsUpdate = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeUsages, barWidthScale, barHeightScale]);

  useEffect(() => {
    setFocusedOuvrageIndex(0);
  }, [selectedWithdrawal]);

  const toggleUsage = (usage) => {
    setActiveUsages((prev) => {
      const base = prev ?? new Set(usageList.map((u) => u.usage));
      const next = new Set(base);
      if (next.has(usage)) next.delete(usage);
      else next.add(usage);
      return next;
    });
  };

  const focusedOuvrage = selectedWithdrawal?.ouvrages?.[focusedOuvrageIndex];
  const climateVariable = climateAxes.variables.find((v) => v.id === climateVariableId) || null;
  const climateScale = climateVariable
    ? climateDomain(climatePoints, climateVariable, climatePeriod, climateAxes.horizons)
    : null;
  const climatePeriodIndex = Math.max(0, climateAxes.periods.indexOf(climatePeriod));

  // Indicateurs, legendes et groupes de selection : tous derives des
  // SPECIFICATIONS embarquees dans le datapack (bornes observees, modalites
  // presentes, couleurs de source), jamais d'un catalogue ecrit en dur ici.
  // Un indicateur ajoute cote pipeline apparait sans toucher a ce fichier.
  const communeIndicator = findIndicator(communeIndicators, communeIndicatorId);
  const communeLegend = useMemo(() => buildLegend(communeIndicator), [communeIndicator]);
  const communeIndicatorGroups = useMemo(
    () => groupedIndicators(communeIndicators), [communeIndicators]
  );

  const geologyIndicator = findIndicator(geologyIndicators, geologyIndicatorId);
  const geologyLegend = useMemo(() => buildLegend(geologyIndicator), [geologyIndicator]);
  const geologyIndicatorGroups = useMemo(
    () => groupedIndicators(geologyIndicators), [geologyIndicators]
  );

  const dischargeMetric = dischargeAxes.metrics.find((m) => m.id === dischargeMetricId) || null;
  const dischargeLegend = useMemo(() => buildLegend(dischargeMetric), [dischargeMetric]);

  // Les postes de culture d'une commune (RA 2010), tries par surface : le
  // detail qui explique la specialisation affichee sur la carte.
  const selectedCultures = selectedCommune?.["agriculture.cultures.postes"] || [];

  const activeDioramaSpot = dioramaSpots.find((spot) => spot.id === activeDioramaId) || null;
  const activeDioramaKind = activeDioramaSpot ? DIORAMA_KINDS[activeDioramaSpot.kind] : null;

  return (
    <div className="terrain3d-page">
      <div className="terrain3d-canvas" ref={mountRef} />

      {status === "loading" && (
        <div className="terrain3d-loading">
          <div className="terrain3d-loading-bar">
            <div className="terrain3d-loading-fill" />
          </div>
          <p>Chargement du relief...</p>
        </div>
      )}

      {status === "error" && (
        <div className="terrain3d-loading">
          <p>
            Donnees introuvables. Lance <code>python3 api/experiments/terrain3d/build_heightmap.py</code>{" "}
            puis recharge la page.
          </p>
        </div>
      )}

      {status === "ready" && (
        <>
          <a href="/" className="terrain3d-back back-button">
            ← Retour
          </a>

          <div className="terrain3d-hud terrain3d-hud-top">
            <span className="terrain3d-badge">EXPERIMENTATION</span>
            <h1>Territoire test — {meta.contour_name || "extrait MNT IGN"}</h1>
            <p className="terrain3d-subtitle">
              BD ALTI V2 25m · découpé par geojson · relief exagéré pour la lecture
            </p>
          </div>

          <div className="terrain3d-hud terrain3d-hud-stats">
            <div className="terrain3d-stat">
              <span className="terrain3d-stat-label">Altitude min</span>
              <span className="terrain3d-stat-value">{meta.zmin.toFixed(0)} m</span>
            </div>
            <div className="terrain3d-stat">
              <span className="terrain3d-stat-label">Altitude max</span>
              <span className="terrain3d-stat-value">{meta.zmax.toFixed(0)} m</span>
            </div>
            {coveragePct != null && (
              <div className="terrain3d-stat">
                <span className="terrain3d-stat-label">Emprise dans le contour</span>
                <span className="terrain3d-stat-value">{coveragePct.toFixed(0)}%</span>
              </div>
            )}
            <div className="terrain3d-legend">
              <div className="terrain3d-legend-gradient" />
              <div className="terrain3d-legend-labels">
                <span>{meta.zmin.toFixed(0)}m</span>
                <span>{meta.zmax.toFixed(0)}m</span>
              </div>
            </div>
          </div>

          <div className="terrain3d-hud terrain3d-hud-controls">
            <label className="terrain3d-control">
              <span>Exagération verticale × {exaggeration.toFixed(1)}</span>
              <input
                type="range"
                min={1}
                max={5}
                step={0.1}
                value={exaggeration}
                onChange={(e) => setExaggeration(parseFloat(e.target.value))}
              />
            </label>
            <label className="terrain3d-control terrain3d-control-checkbox">
              <input
                type="checkbox"
                checked={autoRotate}
                onChange={(e) => setAutoRotate(e.target.checked)}
              />
              <span>Rotation automatique</span>
            </label>
            <label className="terrain3d-control terrain3d-control-checkbox">
              <input
                type="checkbox"
                checked={fogEnabled}
                onChange={(e) => setFogEnabled(e.target.checked)}
              />
              <span>Brouillard</span>
            </label>
            <label className="terrain3d-control terrain3d-control-checkbox">
              <input
                type="checkbox"
                checked={wireframe}
                onChange={(e) => setWireframe(e.target.checked)}
              />
              <span>Mode filaire</span>
            </label>
            <label className="terrain3d-control terrain3d-control-checkbox">
              <input
                type="checkbox"
                checked={showRivers}
                onChange={(e) => setShowRivers(e.target.checked)}
              />
              <span>Rivières</span>
            </label>
            {showRivers && (
              <>
                <label className="terrain3d-control terrain3d-control-nested">
                  <span>Épaisseur des cours d'eau × {riverWidthScale.toFixed(1)}</span>
                  <input
                    type="range"
                    min={0.3}
                    max={3}
                    step={0.1}
                    value={riverWidthScale}
                    onChange={(e) => setRiverWidthScale(parseFloat(e.target.value))}
                  />
                </label>
                {riverLengthBounds && riverLengthCapKm && (
                  <label className="terrain3d-control terrain3d-control-nested">
                    <span>
                      Longueur de référence — épaisseur max au-delà de {Math.round(riverLengthCapKm)} km
                    </span>
                    <input
                      type="range"
                      min={Math.log10(Math.max(1, riverLengthBounds.min))}
                      max={Math.log10(Math.max(riverLengthBounds.max, riverLengthBounds.min + 1))}
                      step={0.01}
                      value={Math.log10(Math.max(1, riverLengthCapKm))}
                      onChange={(e) => setRiverLengthCapKm(Math.pow(10, parseFloat(e.target.value)))}
                    />
                  </label>
                )}
                <label className="terrain3d-control terrain3d-control-checkbox terrain3d-control-nested">
                  <input
                    type="checkbox"
                    checked={showRiverNames}
                    onChange={(e) => setShowRiverNames(e.target.checked)}
                  />
                  <span>Noms au survol</span>
                </label>
              </>
            )}
            <label className="terrain3d-control terrain3d-control-checkbox">
              <input
                type="checkbox"
                checked={showCities}
                onChange={(e) => setShowCities(e.target.checked)}
              />
              <span>Villes</span>
            </label>
            {usageList.length > 0 && (
              <label className="terrain3d-control terrain3d-control-checkbox">
                <input
                  type="checkbox"
                  checked={showWithdrawals}
                  onChange={(e) => setShowWithdrawals(e.target.checked)}
                />
                <span>Prélèvements en eau</span>
              </label>
            )}
            <label className="terrain3d-control terrain3d-control-checkbox">
              <input
                type="checkbox"
                checked={showRainfall}
                onChange={(e) => setShowRainfall(e.target.checked)}
              />
              <span>Pluviométrie (test)</span>
            </label>
            {geologyIndicators.length > 0 && (
              <>
                <label className="terrain3d-control terrain3d-control-checkbox">
                  <input
                    type="checkbox"
                    checked={showGeology}
                    onChange={(e) => setShowGeology(e.target.checked)}
                  />
                  <span>Géologie (BD LISA)</span>
                </label>
                {showGeology && (
                  <>
                    {/* Quatre classements du meme sous-sol : de quelle
                        formation s'agit-il, y a-t-il de l'eau, circule-t-elle
                        vite, est-elle protegee. */}
                    <label className="terrain3d-control terrain3d-control-nested">
                      <span>Classement</span>
                      <select
                        className="terrain3d-select"
                        value={geologyIndicatorId || ""}
                        onChange={(e) => setGeologyIndicatorId(e.target.value)}
                      >
                        {geologyIndicatorGroups.map(({ name, indicators }) => (
                          <optgroup key={name} label={name}>
                            {indicators.map((indicator) => (
                              <option key={indicator.id} value={indicator.id}>{indicator.label}</option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                    </label>
                    <label className="terrain3d-control terrain3d-control-nested">
                      <span>Opacité des couleurs {Math.round(geologyOpacity * 100)} %</span>
                      <input
                        type="range"
                        min={GEOLOGY_MIN_OPACITY}
                        max={GEOLOGY_MAX_OPACITY}
                        step={0.05}
                        value={geologyOpacity}
                        onChange={(e) => setGeologyOpacity(parseFloat(e.target.value))}
                      />
                    </label>
                  </>
                )}
              </>
            )}
            {communes.length > 0 && (
              <>
                <label className="terrain3d-control terrain3d-control-checkbox">
                  <input
                    type="checkbox"
                    checked={showCommunes}
                    onChange={(e) => setShowCommunes(e.target.checked)}
                  />
                  <span>Communes</span>
                </label>
                {showCommunes && (
                  <>
                    <label className="terrain3d-control terrain3d-control-nested">
                      <span>Indicateur cartographié</span>
                      <select
                        className="terrain3d-select"
                        value={communeIndicatorId}
                        onChange={(e) => setCommuneIndicatorId(e.target.value)}
                      >
                        {communeIndicatorGroups.map(({ name, indicators }) => (
                          <optgroup key={name} label={name}>
                            {indicators.map((indicator) => (
                              <option key={indicator.id} value={indicator.id}>
                                {indicator.label}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                    </label>
                    {/* Doser l'aplat de couleur contre l'ombrage du relief :
                        a fond, la carte se lit comme une carte a plat ; en
                        transparence, le modele 3D reprend le dessus. */}
                    <label className="terrain3d-control terrain3d-control-nested">
                      <span>Opacité des couleurs {Math.round(communeOpacity * 100)} %</span>
                      <input
                        type="range"
                        min={COMMUNE_MIN_OPACITY}
                        max={COMMUNE_MAX_OPACITY}
                        step={0.05}
                        value={communeOpacity}
                        onChange={(e) => setCommuneOpacity(parseFloat(e.target.value))}
                      />
                    </label>
                    <label className="terrain3d-control terrain3d-control-checkbox terrain3d-control-nested">
                      <input
                        type="checkbox"
                        checked={showCommuneBorders}
                        onChange={(e) => setShowCommuneBorders(e.target.checked)}
                      />
                      <span>Limites communales</span>
                    </label>
                  </>
                )}
              </>
            )}
            {climateVariable && (
              <>
                <label className="terrain3d-control terrain3d-control-checkbox">
                  <input
                    type="checkbox"
                    checked={showClimate}
                    onChange={(e) => setShowClimate(e.target.checked)}
                  />
                  <span>Climat</span>
                </label>
                {showClimate && (
                  <>
                    <label className="terrain3d-control terrain3d-control-nested">
                      <span>Variable</span>
                      <select
                        className="terrain3d-select"
                        value={climateVariableId}
                        onChange={(e) => setClimateVariableId(e.target.value)}
                      >
                        {climateAxes.variables.map((variable) => (
                          <option key={variable.id} value={variable.id}>{variable.label}</option>
                        ))}
                      </select>
                    </label>
                    <div className="terrain3d-control terrain3d-control-nested">
                      <span>Horizon</span>
                      <div className="terrain3d-segmented" role="group" aria-label="Horizon">
                        {climateAxes.horizons.map((horizon) => (
                          <button
                            key={horizon}
                            type="button"
                            className={`terrain3d-segment${horizon === climateHorizon ? " is-active" : ""}`}
                            onClick={() => setClimateHorizon(horizon)}
                            aria-pressed={horizon === climateHorizon}
                          >
                            {horizonLabel(horizon)}
                          </button>
                        ))}
                      </div>
                    </div>
                    <label className="terrain3d-control terrain3d-control-nested">
                      <span>{periodLabel(climatePeriod)}</span>
                      <input
                        type="range"
                        min={0}
                        max={climateAxes.periods.length - 1}
                        step={1}
                        value={climatePeriodIndex}
                        onChange={(e) => setClimatePeriod(climateAxes.periods[parseInt(e.target.value, 10)])}
                      />
                    </label>
                    <label className="terrain3d-control terrain3d-control-nested">
                      <span>Opacité des couleurs {Math.round(climateOpacity * 100)} %</span>
                      <input
                        type="range"
                        min={CLIMATE_MIN_OPACITY}
                        max={CLIMATE_MAX_OPACITY}
                        step={0.05}
                        value={climateOpacity}
                        onChange={(e) => setClimateOpacity(parseFloat(e.target.value))}
                      />
                    </label>
                    <label className="terrain3d-control terrain3d-control-checkbox terrain3d-control-nested">
                      <input
                        type="checkbox"
                        checked={showClimateStations}
                        onChange={(e) => setShowClimateStations(e.target.checked)}
                      />
                      <span>Points de mesure</span>
                    </label>
                  </>
                )}
              </>
            )}
            {dischargeAxes.metrics.length > 0 && (
              <>
                <label className="terrain3d-control terrain3d-control-checkbox">
                  <input
                    type="checkbox"
                    checked={showDischarge}
                    onChange={(e) => setShowDischarge(e.target.checked)}
                  />
                  <span>Débit des cours d'eau</span>
                </label>
                {showDischarge && (
                  <>
                    <label className="terrain3d-control terrain3d-control-nested">
                      <span>Grandeur</span>
                      <select
                        className="terrain3d-select"
                        value={dischargeMetricId || ""}
                        onChange={(e) => setDischargeMetricId(e.target.value)}
                      >
                        {dischargeAxes.metrics.map((metric) => (
                          <option key={metric.id} value={metric.id}>{metric.label}</option>
                        ))}
                      </select>
                    </label>
                    {/* Quatre horizons ici, contre trois pour le climat : la
                        chronique de debit va jusqu'en 2100. */}
                    <div className="terrain3d-control terrain3d-control-nested">
                      <span>Horizon</span>
                      <div className="terrain3d-segmented" role="group" aria-label="Horizon de débit">
                        {dischargeAxes.horizons.map((horizon) => (
                          <button
                            key={horizon}
                            type="button"
                            className={`terrain3d-segment${horizon === dischargeHorizon ? " is-active" : ""}`}
                            onClick={() => setDischargeHorizon(horizon)}
                            aria-pressed={horizon === dischargeHorizon}
                          >
                            {horizonLabel(horizon)}
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
            <label className="terrain3d-control terrain3d-control-checkbox">
              <input
                type="checkbox"
                checked={showForest}
                onChange={(e) => setShowForest(e.target.checked)}
              />
              <span>Forêt (prototype, zone de test)</span>
            </label>

            <label className="terrain3d-control terrain3d-control-checkbox">
              <input
                type="checkbox"
                checked={showDioramaPins}
                onChange={(e) => setShowDioramaPins(e.target.checked)}
              />
              <span>Dioramas 3D (habillage local)</span>
            </label>
            {showDioramaPins && dioramaSpots.length > 0 && (
              <div className="terrain3d-control terrain3d-control-nested terrain3d-diorama-list">
                {dioramaSpots.map((spot) => (
                  <button
                    key={spot.id}
                    type="button"
                    className={`terrain3d-diorama-button${activeDioramaId === spot.id ? " is-active" : ""}`}
                    style={{ "--diorama-accent": DIORAMA_KINDS[spot.kind].accent }}
                    onClick={() => stateRef.current.focusDiorama?.(spot.id)}
                  >
                    <span className="terrain3d-diorama-dot" />
                    <span className="terrain3d-diorama-button-text">
                      <strong>{DIORAMA_KINDS[spot.kind].label}</strong>
                      <em>{spot.title}</em>
                    </span>
                  </button>
                ))}
                <label className="terrain3d-control terrain3d-control-checkbox">
                  <input
                    type="checkbox"
                    checked={dioramaCloseUp}
                    onChange={(e) => setDioramaCloseUp(e.target.checked)}
                  />
                  <span>Masquer les couches larges pendant le zoom</span>
                </label>
                {activeDioramaId && (
                  <button
                    type="button"
                    className="terrain3d-diorama-reset"
                    onClick={() => stateRef.current.clearDiorama?.()}
                  >
                    ← Revenir à la vue d'ensemble
                  </button>
                )}
              </div>
            )}
          </div>

          {activeDioramaSpot && activeDioramaKind && (
            <div
              className="terrain3d-hud terrain3d-hud-diorama"
              style={{ "--diorama-accent": activeDioramaKind.accent }}
            >
              <span className="terrain3d-diorama-kind">{activeDioramaKind.label}</span>
              <h2>{activeDioramaSpot.title}</h2>
              <p className="terrain3d-diorama-subtitle">{activeDioramaSpot.subtitle}</p>
              {activeDioramaSpot.facts.length > 0 && (
                <div className="terrain3d-diorama-facts">
                  {activeDioramaSpot.facts.map((f) => (
                    <div key={f.label} className="terrain3d-diorama-fact">
                      <span>{f.label}</span>
                      <strong>{f.value}</strong>
                    </div>
                  ))}
                </div>
              )}
              {/* La mise en garde voyage avec le decor : un habillage qui ne
                  dit pas qu'il est un habillage se ferait lire comme une
                  donnee. */}
              <span className="terrain3d-commune-source">{activeDioramaKind.caption}</span>
            </div>
          )}

          {showRainfall && (
            <div className="terrain3d-hud terrain3d-hud-rainlegend">
              <span className="terrain3d-usagelegend-title">
                Pluviométrie — test, données synthétiques
              </span>
              <div className="terrain3d-legend-gradient terrain3d-rain-gradient" />
              <div className="terrain3d-legend-labels">
                <span>Sec</span>
                <span>Humide</span>
              </div>
            </div>
          )}

          {showClimate && climateVariable && (
            <div className="terrain3d-hud terrain3d-hud-climatelegend">
              <span className="terrain3d-usagelegend-title">
                {climateVariable.label} — {horizonLabel(climateHorizon)}, {periodLabel(climatePeriod).toLowerCase()}
              </span>
              <div
                className="terrain3d-legend-gradient"
                style={{ background: rampCss(climateVariable.ramp) }}
              />
              <div className="terrain3d-legend-labels">
                <span>{formatClimateValue(climateVariable, climateScale.min)}</span>
                <span>{formatClimateValue(climateVariable, climateScale.max)}</span>
              </div>
              <span className="terrain3d-commune-source">
                Échelle commune aux {climateAxes.horizons.length} horizons pour cette période, sinon
                le changement serait invisible. Nappe interpolée depuis {climatePoints.length} points.
              </span>
            </div>
          )}

          {showCommunes && communeIndicator && (
            <div className="terrain3d-hud terrain3d-hud-communelegend">
              <IndicatorLegend indicator={communeIndicator} legend={communeLegend} />
            </div>
          )}

          {showGeology && geologyIndicator && (
            <div className="terrain3d-hud terrain3d-hud-geologylegend">
              <IndicatorLegend indicator={geologyIndicator} legend={geologyLegend} />
            </div>
          )}

          {showDischarge && dischargeMetric && (
            <div className="terrain3d-hud terrain3d-hud-dischargelegend">
              <IndicatorLegend
                indicator={dischargeMetric}
                legend={dischargeLegend}
                subtitle={`${horizonLabel(dischargeHorizon)} · ${dischargeStations.length} stations`}
                note={`Échelle commune aux ${dischargeAxes.horizons.length} horizons, sinon l'évolution serait invisible. La hauteur des marqueurs suit la même valeur que leur couleur.`}
              />
            </div>
          )}

          {usageList.length > 0 && showWithdrawals && (
            <div className="terrain3d-hud terrain3d-hud-usagelegend">
              <span className="terrain3d-usagelegend-title">Usage (cliquer pour filtrer)</span>
              {usageList.map(({ usage, color }) => {
                const isActive = activeUsages ? activeUsages.has(usage) : true;
                return (
                  <button
                    key={usage}
                    className="terrain3d-usage-chip"
                    style={{
                      "--chip-color": color,
                      opacity: isActive ? 1 : 0.35,
                    }}
                    onClick={() => toggleUsage(usage)}
                  >
                    <span className="terrain3d-usage-dot" />
                    {usage}
                  </button>
                );
              })}
              <div className="terrain3d-usage-sliders">
                <label className="terrain3d-control">
                  <span>Largeur des barres × {barWidthScale.toFixed(1)}</span>
                  <input
                    type="range"
                    min={0.4}
                    max={2.5}
                    step={0.1}
                    value={barWidthScale}
                    onChange={(e) => setBarWidthScale(parseFloat(e.target.value))}
                  />
                </label>
                <label className="terrain3d-control">
                  <span>Hauteur max × {barHeightScale.toFixed(1)}</span>
                  <input
                    type="range"
                    min={0.4}
                    max={3}
                    step={0.1}
                    value={barHeightScale}
                    onChange={(e) => setBarHeightScale(parseFloat(e.target.value))}
                  />
                </label>
              </div>
            </div>
          )}

          <div className="terrain3d-hud terrain3d-hud-hint">
            Glisser = orbiter · molette = zoom · cliquer un marqueur pour le
            détailler
          </div>

          {showRiverNames && hoveredRiver && (
            <div
              className="terrain3d-river-tooltip"
              style={{ left: hoveredRiver.clientX, top: hoveredRiver.clientY }}
            >
              {hoveredRiver.name}
              {hoveredRiver.length_km != null && (
                <span className="terrain3d-river-tooltip-length"> · {hoveredRiver.length_km.toFixed(1)} km</span>
              )}
            </div>
          )}

          {showCommunes && hoveredCommune && (
            <div
              className="terrain3d-river-tooltip"
              style={{ left: hoveredCommune.clientX, top: hoveredCommune.clientY }}
            >
              {hoveredCommune.commune.nom_commune}
              <span className="terrain3d-river-tooltip-length">
                {" "}· {formatIndicatorValue(communeIndicator, hoveredCommune.commune)}
              </span>
            </div>
          )}

          {showGeology && hoveredGeology && (
            <div
              className="terrain3d-river-tooltip"
              style={{ left: hoveredGeology.clientX, top: hoveredGeology.clientY }}
            >
              {hoveredGeology.entity["theme.libelle"] || "Entité"}
              <span className="terrain3d-river-tooltip-length">
                {" "}· {formatIndicatorValue(geologyIndicator, hoveredGeology.entity)}
              </span>
              <div className="terrain3d-tooltip-detail">{hoveredGeology.entity.libelle}</div>
            </div>
          )}

          {showClimate && selectedStation && climateVariable && (
            <div className="terrain3d-hud terrain3d-hud-stationinfo">
              <button
                className="terrain3d-cityinfo-close"
                onClick={() => setSelectedStation(null)}
                aria-label="Fermer"
              >
                ×
              </button>
              <span className="terrain3d-badge">Point de mesure</span>
              <h2>{climateVariable.label}</h2>
              <p className="terrain3d-subtitle">
                {selectedStation.latitude?.toFixed(3)}° N · {selectedStation.longitude?.toFixed(3)}° E
              </p>

              {/* C'est ici que la donnée mensuelle prend son sens : la carte
                  ne montre qu'un mois à la fois, la courbe montre l'année
                  entière et tous les horizons d'un coup. */}
              <div className="terrain3d-station-chart">
                <Line
                  data={{
                    labels: CLIMATE_MONTH_LABELS,
                    datasets: stationMonthlySeries(selectedStation, climateVariable, climateAxes.horizons).map(
                      (serie, i) => ({
                        label: horizonLabel(serie.horizon),
                        data: serie.values,
                        borderColor: HORIZON_COLORS[i % HORIZON_COLORS.length],
                        backgroundColor: HORIZON_COLORS[i % HORIZON_COLORS.length],
                        borderWidth: 2,
                        pointRadius: 0,
                        pointHoverRadius: 4,
                        tension: 0.35,
                      })
                    ),
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: { mode: "index", intersect: false },
                    plugins: {
                      legend: { display: false },
                      // Le plugin d'étiquettes est enregistré globalement :
                      // sans ça, toutes les valeurs se superposeraient.
                      datalabels: { display: false },
                      tooltip: {
                        callbacks: {
                          label: (ctx) => `${ctx.dataset.label} : ${formatClimateValue(climateVariable, ctx.parsed.y)}`,
                        },
                      },
                    },
                    scales: {
                      x: { ticks: { color: "#c8dbe0", font: { size: 10 } }, grid: { display: false } },
                      y: {
                        ticks: { color: "#c8dbe0", font: { size: 10 } },
                        grid: { color: "rgba(255,255,255,0.08)" },
                      },
                    },
                  }}
                />
              </div>

              <dl className="terrain3d-commune-stats">
                {climateAxes.horizons.map((horizon, i) => (
                  <div key={horizon} className={horizon === climateHorizon ? "is-current" : ""}>
                    <dt>
                      <span
                        className="terrain3d-usage-dot"
                        style={{ "--chip-color": HORIZON_COLORS[i % HORIZON_COLORS.length] }}
                      />
                      {horizonLabel(horizon)} · cumul annuel
                    </dt>
                    <dd>
                      {formatClimateValue(
                        climateVariable,
                        climateValueAt(selectedStation, climateVariable, horizon, "annuel")
                      )}
                    </dd>
                  </div>
                ))}
              </dl>

              {/* Le bilan de l'horizon affiché : la carte montre un flux à la
                  fois, ces quelques chiffres disent où l'eau part réellement. */}
              {stationScalars(selectedStation, climateHorizon).length > 0 && (
                <>
                  <h3 className="terrain3d-panel-subhead">
                    Bilan hydrique · {horizonLabel(climateHorizon)}
                  </h3>
                  <dl className="terrain3d-commune-stats">
                    {stationScalars(selectedStation, climateHorizon).map((scalar) => (
                      <div key={scalar.id} title={scalar.help || undefined}>
                        <dt>{scalar.label}</dt>
                        <dd>{scalar.text}</dd>
                      </div>
                    ))}
                  </dl>
                </>
              )}
            </div>
          )}

          {showCommunes && selectedCommune && (
            <div className="terrain3d-hud terrain3d-hud-communeinfo">
              <button
                className="terrain3d-cityinfo-close"
                onClick={() => setSelectedCommune(null)}
                aria-label="Fermer"
              >
                ×
              </button>
              <span className="terrain3d-badge">{selectedCommune.code_insee}</span>
              <h2>{selectedCommune.nom_commune}</h2>
              <p className="terrain3d-subtitle">
                {formatIndicatorValue(findIndicator(communeIndicators, "population.habitants"), selectedCommune)}
                {selectedCommune["population.rang_departement"] != null &&
                  ` · ${selectedCommune["population.rang_departement"]}ᵉ du département`}
                {selectedCommune.surface_ha != null && ` · ${Math.round(selectedCommune.surface_ha)} ha`}
              </p>

              {selectedCommune["agriculture.specialisation.libelle"] && (
                <p className="terrain3d-commune-spec">
                  <span
                    className="terrain3d-usage-dot"
                    style={{ "--chip-color": selectedCommune["agriculture.specialisation.couleur"] || "#8d9aa0" }}
                  />
                  {selectedCommune["agriculture.specialisation.libelle"]}
                </p>
              )}

              {/* Le clic sert à comparer une commune sur TOUT ce que le
                  territoire renseigne, pas seulement sur l'indicateur qui
                  colore la carte. Les groupes viennent du datapack : ajouter
                  une thématique côté pipeline la fait apparaître ici. */}
              {communeIndicatorGroups.map(({ name, indicators }) => {
                const rows = indicators.filter((indicator) => selectedCommune[indicator.id] != null);
                if (!rows.length) return null;
                return (
                  <div key={name} className="terrain3d-commune-group">
                    <h3 className="terrain3d-panel-subhead">{name}</h3>
                    <dl className="terrain3d-commune-stats">
                      {rows.map((indicator) => (
                        <div
                          key={indicator.id}
                          className={indicator.id === communeIndicatorId ? "is-current" : ""}
                        >
                          <dt>{indicator.label}</dt>
                          <dd>{formatIndicatorValue(indicator, selectedCommune)}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                );
              })}

              {/* Les postes de culture ne se cartographient pas (un tableau
                  n'a pas de couleur) mais ils expliquent la spécialisation
                  affichée : c'est leur place, dans la fiche. */}
              {selectedCultures.length > 0 && (
                <>
                  <h3 className="terrain3d-panel-subhead">
                    Cultures {selectedCommune["agriculture.cultures.millesime"] &&
                      `(RA ${selectedCommune["agriculture.cultures.millesime"]})`}
                  </h3>
                  <div className="terrain3d-culture-list">
                    {selectedCultures.map((poste) => (
                      <div key={poste.libelle} className="terrain3d-culture-row">
                        <span className="terrain3d-culture-name">{poste.libelle}</span>
                        <span className="terrain3d-culture-bar">
                          <span style={{ width: `${Math.min(100, poste.part_sau_pct || 0)}%` }} />
                        </span>
                        <span className="terrain3d-culture-value">
                          {poste.surface_ha == null
                            ? "secret stat."
                            : `${formatNumber(poste.part_sau_pct, "decimal")} %`}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {showDischarge && selectedDischarge && (
            <div className="terrain3d-hud terrain3d-hud-stationinfo">
              <button
                className="terrain3d-cityinfo-close"
                onClick={() => setSelectedDischarge(null)}
                aria-label="Fermer"
              >
                ×
              </button>
              <span className="terrain3d-badge">Station hydrométrique</span>
              <h2>{selectedDischarge.cours_eau}</h2>
              <p className="terrain3d-subtitle">
                {selectedDischarge.lieu} · {selectedDischarge.code_station}
              </p>

              {/* Le régime mensuel, tous horizons superposés. En coefficients
                  de Pardé (débit du mois ÷ module), deux rivières de tailles
                  très différentes deviennent comparables : au-dessus de 1
                  c'est les hautes eaux, en dessous l'étiage. */}
              <label className="terrain3d-control terrain3d-control-checkbox">
                <input
                  type="checkbox"
                  checked={dischargeNormalized}
                  onChange={(e) => setDischargeNormalized(e.target.checked)}
                />
                <span>Coefficients de Pardé (débit ÷ module)</span>
              </label>

              <div className="terrain3d-station-chart">
                <Line
                  data={{
                    labels: DISCHARGE_MONTH_LABELS,
                    datasets: stationMonthlyDischarge(
                      selectedDischarge, dischargeAxes.horizons, { normalized: dischargeNormalized }
                    ).map((serie, i) => ({
                      label: horizonLabel(serie.horizon),
                      data: serie.values,
                      borderColor: HORIZON_COLORS[i % HORIZON_COLORS.length],
                      backgroundColor: HORIZON_COLORS[i % HORIZON_COLORS.length],
                      borderWidth: 2,
                      pointRadius: 0,
                      pointHoverRadius: 4,
                      tension: 0.35,
                    })),
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: { mode: "index", intersect: false },
                    plugins: {
                      legend: { display: false },
                      datalabels: { display: false },
                      tooltip: {
                        callbacks: {
                          label: (ctx) =>
                            `${ctx.dataset.label} : ${formatNumber(ctx.parsed.y, "precise")}${
                              dischargeNormalized ? "" : " m³/s"
                            }`,
                        },
                      },
                    },
                    scales: {
                      x: { ticks: { color: "#c8dbe0", font: { size: 10 } }, grid: { display: false } },
                      y: {
                        ticks: { color: "#c8dbe0", font: { size: 10 } },
                        grid: { color: "rgba(255,255,255,0.08)" },
                      },
                    },
                  }}
                />
              </div>

              {/* Toutes les grandeurs déclarées, à tous les horizons : c'est
                  ici que se lit l'aggravation de l'étiage d'ici 2100. */}
              {dischargeAxes.metrics.map((metric) => (
                <div key={metric.id} className="terrain3d-commune-group">
                  <h3 className="terrain3d-panel-subhead">{metric.label}</h3>
                  <dl className="terrain3d-commune-stats">
                    {dischargeAxes.horizons.map((horizon, i) => (
                      <div key={horizon} className={horizon === dischargeHorizon ? "is-current" : ""}>
                        <dt>
                          <span
                            className="terrain3d-usage-dot"
                            style={{ "--chip-color": HORIZON_COLORS[i % HORIZON_COLORS.length] }}
                          />
                          {horizonLabel(horizon)}
                        </dt>
                        <dd>
                          {formatDischargeValue(metric, dischargeValueAt(selectedDischarge, metric, horizon))}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              ))}

              <span className="terrain3d-commune-source">
                DRIAS-EAU / Explore2, chaîne SIM2, scénario RCP 8.5 — débits simulés,
                pas observés. Les indicateurs annuels portent sur l'année hydrologique
                (1ᵉʳ août → 31 juillet).
              </span>
            </div>
          )}

          {selectedCity && (
            <div className="terrain3d-hud terrain3d-hud-cityinfo">
              <button
                className="terrain3d-cityinfo-close"
                onClick={() => setSelectedCity(null)}
                aria-label="Fermer"
              >
                ×
              </button>
              <span className="terrain3d-badge">
                {selectedCity.code_insee || selectedCity.code_postal}
              </span>
              <h2>{selectedCity.name}</h2>
              <p>
                {selectedCity.population != null
                  ? `${formatNumber(selectedCity.population, "count")} habitants · ${selectedCity.rank}ᵉ commune du département`
                  : selectedCity.statut}
              </p>
            </div>
          )}

          {selectedWithdrawal && focusedOuvrage && (
            <div className="terrain3d-hud terrain3d-hud-withdrawalinfo">
              <button
                className="terrain3d-cityinfo-close"
                onClick={() => setSelectedWithdrawal(null)}
                aria-label="Fermer"
              >
                ×
              </button>
              <span className="terrain3d-badge">{selectedWithdrawal.usage}</span>
              <h2>{selectedWithdrawal.commune}</h2>
              {selectedWithdrawal.ouvrages.length > 1 && (
                <p className="terrain3d-subtitle">
                  {selectedWithdrawal.ouvrages.length} ouvrages à cet endroit (coordonnées communales
                  partagées) — {formatVolume(selectedWithdrawal.total_volume)} au total
                </p>
              )}

              {selectedWithdrawal.ouvrages.length > 1 && (
                <div className="terrain3d-ouvrage-list">
                  {selectedWithdrawal.ouvrages.map((ouv, i) => (
                    <button
                      key={ouv.code_ouvrage}
                      className={`terrain3d-ouvrage-item${i === focusedOuvrageIndex ? " active" : ""}`}
                      onClick={() => setFocusedOuvrageIndex(i)}
                    >
                      <span className="terrain3d-ouvrage-name">{ouv.nom || ouv.code_ouvrage}</span>
                      <span className="terrain3d-ouvrage-vol">{formatVolume(ouv.last_volume)}</span>
                    </button>
                  ))}
                </div>
              )}

              <h2 className="terrain3d-ouvrage-focus-name">{focusedOuvrage.nom || "Ouvrage"}</h2>

              <div className="terrain3d-withdrawal-stats">
                <div>
                  <span className="terrain3d-stat-label">Dernier volume ({focusedOuvrage.last_annee})</span>
                  <span className="terrain3d-stat-value">{formatVolume(focusedOuvrage.last_volume)}</span>
                </div>
                {focusedOuvrage.trend_pct != null && (
                  <div>
                    <span className="terrain3d-stat-label">Évolution</span>
                    <span className="terrain3d-stat-value">
                      {focusedOuvrage.trend_pct > 0 ? "+" : ""}
                      {focusedOuvrage.trend_pct}%
                    </span>
                  </div>
                )}
              </div>

              {focusedOuvrage.series?.length > 1 && (
                <div className="terrain3d-withdrawal-chart">
                  <Bar
                    data={{
                      labels: focusedOuvrage.series.map((s) => s.annee),
                      datasets: [
                        {
                          data: focusedOuvrage.series.map((s) => s.volume),
                          backgroundColor:
                            usageList.find((u) => u.usage === selectedWithdrawal.usage)?.color || "#54a8ba",
                          borderRadius: 3,
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { display: false },
                        tooltip: { callbacks: { label: (ctx) => formatVolume(ctx.parsed.y) } },
                        datalabels: {
                          color: "#0a1a24",
                          font: { size: 10, weight: "700" },
                          anchor: "end",
                          align: "start",
                          formatter: (v) => formatVolume(v),
                        },
                      },
                      scales: {
                        x: { ticks: { color: "#c8dbe0", font: { size: 10 } }, grid: { display: false } },
                        y: {
                          ticks: { color: "#c8dbe0", font: { size: 10 }, callback: (v) => formatVolume(v) },
                          grid: { color: "rgba(255,255,255,0.08)" },
                        },
                      },
                    }}
                  />
                </div>
              )}

              {focusedOuvrage.uri && (
                <a
                  href={focusedOuvrage.uri}
                  target="_blank"
                  rel="noreferrer"
                  className="terrain3d-withdrawal-link"
                >
                  Fiche officielle Sandre ↗
                </a>
              )}
            </div>
          )}

          {usageList.length > 0 && showWithdrawals && (
            <div className="terrain3d-disclaimer">
              Données BNPE (Hub'Eau) issues de la gestion des redevances : les volumes
              inférieurs à 10 000 m³/an et les usages exonérés n'apparaissent pas dans
              cette base — ce n'est pas un inventaire exhaustif. La position de
              nombreux ouvrages correspond au centroïde de la commune, pas à leur
              localisation réelle : ces ouvrages sont regroupés sous un même marqueur
              (détail au clic) plutôt que dispersés artificiellement.
            </div>
          )}
        </>
      )}
    </div>
  );
}
